/**
 * ChatDock — the SHARED, reusable chat UI: conversation list + active thread + composer +
 * a "new chat" picker. Rendered in two containers:
 *   - <ChatDrawer/>  : a slide-in side panel toggled from the navbar (any page).
 *   - /chat full page (pages/Chat.tsx) : a two-pane layout.
 * Both reuse <ChatDockInner/> so the chat logic lives in one place.
 *
 * Model: a conversation targets one or more AI REPS. Any human posts as the GLOBAL identity
 * (the Navbar "who am I"). Conversations can be scoped to an initiative.
 *
 * Attribution is explicit: AI reps render as "Zee · AI" in a distinct PURPLE; humans render
 * solid, by their name. An AI message NEVER looks like it came from the real person.
 *
 * Per AI-message actions: Give feedback / Teach, Endorse, and Bring in the real ___.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useChat } from '../context/ChatContext';
import { useIdentity, IDENTITY_PEOPLE, personLabel } from '../context/IdentityContext';
import { authHeaders } from '../services/auth';
import { useTaskModal } from '../context/TaskModalContext';
import { notifyTaskModalChanged } from './TaskModal';
import { listInitiatives, InitiativeSummary, getAvailableModels, API_BASE_URL } from '../services/api';
import { detectFilePaths } from '../utils/detectFilePaths';
import { detectLinks } from '../utils/detectLinks';
import { MessageLink } from './LinkedText';
import {
  ChatAgent,
  ChecklistItem,
  ConversationDetail,
  ConversationMessage,
  ConversationSummary,
  QuestOption,
  addConversationParticipant,
  createConversation,
  deleteConversation,
  deleteMessage,
  editMessageStream,
  endorseMessage,
  getConversation,
  listChatAgents,
  listConversations,
  listQuests,
  openDirectConversation,
  orchestrateMessage,
  postChatMessage,
  postChatMessageStream,
  removeConversationParticipant,
  requestHuman,
  retryReply,
  runDeep,
  setChecklistItemChecked,
  setConversationPinned,
  setConversationQuest,
  teachMessage,
  updateConversationModel,
} from '../services/chat.service';

const AI_COLOR = '#8b5cf6';

// ---- small helpers ---------------------------------------------------------

const initials = (ref: string): string => (ref || '?').slice(0, 2).toUpperCase();
const cap = personLabel;

// Format a message's created_at (ISO datetime from the backend) as a concise day + time, e.g.
// "Jun 4, 3:21 PM". Returns '' for a missing/invalid value (so nothing renders).
const fmtTimestamp = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

// DEEP MODE — light heuristic mirroring the backend's _is_actionable: does a human message read
// as an explicit "do work" request (vs. chit-chat)? Used only to surface the non-intrusive
// "🔧 Run this for real?" affordance on a rep's quick reply; it never auto-spawns.
// Verbs that mean "go PRODUCE or CHANGE something" — the only ones worth offering to re-run as a
// tracked task. Read-only verbs ('research', 'find', 'look up', 'review', 'analyze', 'summarize',
// 'outline') used to be in this list, which put a "Run this for real?" button under the reply to
// every ordinary question ("summarize the meeting notes", "find the lead count"). One click there
// posts the question again and creates a task with NO planner gate at all, so a question the rep
// had already answered correctly became a task anyway. Asking to be TOLD something is answered in
// the chat; if the user genuinely wants a deliverable, they say "write"/"create"/"draft".
const WORK_TRIGGERS = [
  'draft', 'write', 'create', 'build', 'make', 'send', 'post', 'publish', 'schedule',
  'fix', 'implement', 'add', 'update', 'generate', 'prepare', 'compose', 'set up',
  'put together', 'go ahead and', 'kick off', 'spin up',
];
const looksLikeWork = (text?: string | null): boolean => {
  const t = (text || '').trim().toLowerCase();
  if (!t) return false;
  const head = t.slice(0, 40);
  if (WORK_TRIGGERS.some((w) => head.includes(w))) return true;
  const first = t.split(/\s+/)[0] || '';
  return WORK_TRIGGERS.includes(first);
};
// A deep-mode rep message (the "running as task #N" link or the result reflection) — don't show
// the "run this for real?" affordance on these.
const isDeepRepMessage = (text?: string | null): boolean => {
  const t = (text || '').trimStart();
  return t.startsWith('🔧') || t.startsWith('✅') || t.startsWith('⚠️');
};

// A colored avatar bubble; AI reps (purple) and humans (primary) are visually distinct.
const Avatar: React.FC<{ refName: string; isAi: boolean }> = ({ refName, isAi }) => {
  const { theme } = useTheme();
  return (
    <div
      title={`${cap(refName)} ${isAi ? '(AI)' : '(human)'}`}
      style={{
        flex: '0 0 auto',
        width: 28,
        height: 28,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 11,
        fontWeight: 700,
        color: '#fff',
        backgroundColor: isAi ? AI_COLOR : theme.primary,
        border: isAi ? '2px solid #c4b5fd' : `2px solid ${theme.primary}`,
      }}
    >
      {initials(refName)}
    </div>
  );
};

// A tiny toast surfaced after collaboration actions.
const Toast: React.FC<{ msg: string; onDone: () => void }> = ({ msg, onDone }) => {
  const { theme } = useTheme();
  useEffect(() => {
    const t = setTimeout(onDone, 2600);
    return () => clearTimeout(t);
  }, [onDone]);
  return (
    <div
      style={{
        position: 'absolute',
        left: '50%',
        bottom: 84,
        transform: 'translateX(-50%)',
        zIndex: 50,
        backgroundColor: theme.text,
        color: theme.background,
        padding: '8px 14px',
        borderRadius: 8,
        fontSize: 13,
        fontWeight: 600,
        boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
        maxWidth: '90%',
        textAlign: 'center',
      }}
    >
      {msg}
    </div>
  );
};

// Fetches the file at `path` from the backend's /download endpoint and saves it via a hidden
// anchor (forces a "Save As" rather than a new tab). Throws with a short, factual message on any
// non-2xx response or network failure, so the caller can surface it inline.
async function downloadFile(path: string): Promise<void> {
  const url = `${API_BASE_URL}/download?path=${encodeURIComponent(path)}`;
  let response: Response;
  try {
    response = await fetch(url, { headers: authHeaders() });
  } catch {
    throw new Error("Couldn't download this file.");
  }
  if (!response.ok) {
    let detail = "Couldn't download this file.";
    try {
      const body = await response.json();
      if (body?.detail) detail = body.detail;
    } catch {
      // no JSON body; keep the default message
    }
    throw new Error(detail);
  }
  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);
  const filename = path.split('/').pop() || 'download';
  const anchor = document.createElement('a');
  anchor.href = blobUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(blobUrl);
}

// A file path mentioned in a rep's message, shown as plain text immediately followed by a small,
// secondary-styled inline "Download" button that pulls the file straight from the server.
const DownloadablePath: React.FC<{ path: string }> = ({ path }) => {
  const { theme } = useTheme();
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auto-clear the inline error after a few seconds so it doesn't linger in the transcript.
  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(null), 4000);
    return () => clearTimeout(t);
  }, [error]);

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation(); // don't toggle the bubble's tap-to-show-actions
    if (downloading) return;
    setDownloading(true);
    setError(null);
    try {
      await downloadFile(path);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't download this file.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <span style={{ wordBreak: 'break-all' }}>
      <span>{path}</span>{' '}
      <button
        type="button"
        onClick={handleClick}
        disabled={downloading}
        title={`Download ${path.split('/').pop()}`}
        style={{
          background: 'none',
          border: `1px solid ${theme.border}`,
          borderRadius: 4,
          color: theme.textSecondary,
          cursor: downloading ? 'wait' : 'pointer',
          fontSize: 10,
          fontWeight: 700,
          padding: '0 5px',
          verticalAlign: 'middle',
          whiteSpace: 'nowrap',
        }}
      >
        {downloading ? '↓ …' : '⬇ Download'}
      </button>
      {error && (
        <span style={{ fontSize: 11, color: theme.error, marginLeft: 5, whiteSpace: 'nowrap' }}>
          {error}
        </span>
      )}
    </span>
  );
};

// File paths and links are detected independently, then merged into one ordered, non-overlapping
// list so a single pass can splice both into a run of text. A path inside a link's span loses.
// Pulled out of MessageText so a checklist item's own label text (below) can reuse it per-line.
function renderInlineSegments(content: string, keyPrefix: string): React.ReactNode {
  const paths = detectFilePaths(content).map((m) => ({ kind: 'path' as const, ...m }));
  const links = detectLinks(content).map((m) => ({ kind: 'link' as const, ...m }));
  const segments = [
    ...links,
    ...paths.filter((p) => !links.some((l) => p.start < l.end && p.end > l.start)),
  ].sort((a, b) => a.start - b.start);

  if (segments.length === 0) return content;

  const nodes: React.ReactNode[] = [];
  let cursor = 0;
  segments.forEach((m, i) => {
    if (m.start > cursor) nodes.push(content.slice(cursor, m.start));
    nodes.push(
      m.kind === 'link' ? (
        <MessageLink key={`${keyPrefix}-${m.start}-${i}`} href={m.href} label={m.label} />
      ) : (
        <DownloadablePath key={`${keyPrefix}-${m.start}-${i}`} path={m.path} />
      )
    );
    cursor = m.end;
  });
  if (cursor < content.length) nodes.push(content.slice(cursor));
  return nodes;
}

// A line matching this (mirrors CHECKLIST_LINE_RE in chat_service.py) is rendered as an
// interactive checkbox instead of literal text; the leading marker/bullet is only used to
// FIND the line, its checked state comes from the matching ChecklistItem, not re-parsed here.
const CHECKLIST_LINE_RE = /^[ \t]*(?:[-*][ \t]+)?\[[ xX]\]/;

// One interactive `- [ ]`/`- [x]` checklist row. Controlled off `item` (no local state): the
// parent (ChatDockInner.handleToggleChecklistItem) flips it optimistically and reconciles/reverts
// on the API response, so this just reflects whatever it's given. `onToggle` is omitted for
// read-only contexts (e.g. superseded message versions), which renders a disabled checkbox.
const ChecklistCheckbox: React.FC<{
  item: ChecklistItem;
  onToggle?: (item: ChecklistItem, checked: boolean) => void;
}> = ({ item, onToggle }) => {
  const { theme } = useTheme();
  return (
    <div
      onClick={(e) => e.stopPropagation()} // don't let the click also toggle the bubble's reply target
      style={{ display: 'flex', alignItems: 'flex-start', gap: 6, margin: '2px 0' }}
    >
      <input
        type="checkbox"
        checked={item.checked}
        disabled={!onToggle}
        onChange={(e) => onToggle && onToggle(item, e.target.checked)}
        style={{ marginTop: 3, flexShrink: 0, accentColor: theme.primary, cursor: onToggle ? 'pointer' : 'default' }}
      />
      <span style={{ textDecoration: item.checked ? 'line-through' : 'none', opacity: item.checked ? 0.65 : 1 }}>
        {renderInlineSegments(item.text, `chk-${item.id}`)}
      </span>
    </div>
  );
};

// Renders a plain-text message, splicing in a <DownloadablePath/> immediately after every
// absolute file path it mentions, a clickable <MessageLink/> for every URL (bare or
// markdown-style), and an interactive <ChecklistCheckbox/> for every `- [ ]`/`- [x]` line when
// `checklistItems` is given (the message's parsed+persisted checkbox state from the backend;
// zipped against the checklist lines found in `text`, in document order). Used everywhere a
// rep's message text is displayed (the live bubble, earlier versions, and the ephemeral
// orchestrator status line) so Joshua can grab a generated file without SSH'ing in, follow a
// link without copy-pasting it, and check off a task list item in place.
const MessageText: React.FC<{
  text: string | null | undefined;
  checklistItems?: ChecklistItem[];
  onToggleChecklistItem?: (item: ChecklistItem, checked: boolean) => void;
}> = ({ text, checklistItems, onToggleChecklistItem }) => {
  const content = text || '';

  if (checklistItems && checklistItems.length > 0) {
    const lines = content.split('\n');
    let cursor = 0;
    return (
      <>
        {lines.map((line, i) => {
          const item = CHECKLIST_LINE_RE.test(line) ? checklistItems[cursor] : undefined;
          if (item) {
            cursor += 1;
            return <ChecklistCheckbox key={`li-${i}`} item={item} onToggle={onToggleChecklistItem} />;
          }
          // Non-checklist line (or a checklist-shaped line with no matching item, e.g. a stale
          // resync) — render as normal text, one block per line so spacing matches the original.
          return (
            <div key={`li-${i}`}>{line ? renderInlineSegments(line, `li-${i}`) : ' '}</div>
          );
        })}
      </>
    );
  }

  return <>{renderInlineSegments(content, 'seg')}</>;
};

// A lightweight "{Rep} · AI is typing…" placeholder bubble, shown per responding rep while its
// reply streams in. Purple (matching AI styling), with a subtle animated ellipsis. Constrained to
// the narrow drawer (max-width, word-break) so it never overflows.
const TypingPlaceholder: React.FC<{ rep: string }> = ({ rep }) => {
  const { theme } = useTheme();
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      {/* A tiny one-time keyframe for the subtle ellipsis pulse (no external CSS needed). */}
      <style>{`@keyframes chatdockTypingPulse{0%,100%{opacity:.35}50%{opacity:1}}.chatdock-typing-dots{animation:chatdockTypingPulse 1.2s ease-in-out infinite}`}</style>
      <Avatar refName={rep} isAi />
      <div style={{ maxWidth: '78%', minWidth: 0 }}>
        <div
          style={{
            fontSize: 11,
            marginBottom: 2,
            color: AI_COLOR,
            fontWeight: 700,
          }}
        >
          {cap(rep)} · AI
        </div>
        <div
          style={{
            padding: '10px 14px',
            borderRadius: '14px 14px 14px 2px',
            backgroundColor: AI_COLOR + '14',
            color: theme.textSecondary,
            border: `1px solid ${AI_COLOR}55`,
            fontSize: 14,
            fontStyle: 'italic',
            wordBreak: 'break-word',
          }}
        >
          is typing<span className="chatdock-typing-dots">…</span>
        </div>
      </div>
    </div>
  );
};

const initiativeName = (inits: InitiativeSummary[], id?: string | null): string => {
  if (!id) return '';
  const found = inits.find((i) => i.id === id);
  return found ? found.name || found.id : id;
};

// ---- New chat panel --------------------------------------------------------

const NewChatPanel: React.FC<{
  agents: ChatAgent[];
  initiatives: InitiativeSummary[];
  // Selectable quests, loaded once by the parent. Empty when the Teams API is unreachable.
  quests: QuestOption[];
  creator: string;
  // Pre-seed the scope selector (e.g. from the list's active initiative filter) so the user
  // doesn't reselect. The selector stays fully editable.
  defaultInitiativeId?: string;
  onCreated: (conv: ConversationSummary) => void;
  onCancel: () => void;
}> = ({ agents, initiatives, quests, creator, defaultInitiativeId, onCreated, onCancel }) => {
  const { theme } = useTheme();
  const [selectedAis, setSelectedAis] = useState<string[]>([]);
  const [initiativeId, setInitiativeId] = useState<string>(defaultInitiativeId || '');
  const [questId, setQuestId] = useState<string>('');
  const [title, setTitle] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleAi = (ref: string) =>
    setSelectedAis((arr) => (arr.includes(ref) ? arr.filter((r) => r !== ref) : [...arr, ref]));

  const create = async () => {
    if (selectedAis.length === 0) {
      setError('Pick at least one AI rep.');
      return;
    }
    setCreating(true);
    setError(null);
    try {
      const conv = await createConversation(creator, selectedAis, {
        initiativeId: initiativeId || undefined,
        questId: questId || undefined,
        title: title.trim() || undefined,
      });
      onCreated(conv);
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Could not create the conversation.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div
      style={{
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        overflowY: 'auto',
        overflowX: 'hidden',
        width: '100%',
        maxWidth: '100%',
        // min-width:0 so a long selected initiative label can't stretch this column past the
        // panel width on the full /chat page (where this panel lives in a flex:1 main pane).
        minWidth: 0,
        boxSizing: 'border-box',
      }}
    >
      <div style={{ color: theme.text, fontWeight: 700, fontSize: 15 }}>New conversation</div>

      <div style={{ minWidth: 0, maxWidth: '100%' }}>
        <div style={{ color: theme.textSecondary, fontSize: 12, marginBottom: 6 }}>
          Chat with these AI reps — pick one for a direct chat, or several for a group.
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, maxWidth: '100%' }}>
          {agents.map((a) => {
            const active = selectedAis.includes(a.ref);
            return (
              <button
                key={a.ref}
                onClick={() => toggleAi(a.ref)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 12px',
                  borderRadius: 999,
                  border: `1px solid ${active ? AI_COLOR : theme.border}`,
                  backgroundColor: active ? AI_COLOR : theme.surface,
                  color: active ? '#fff' : theme.text,
                  cursor: 'pointer',
                  fontSize: 13,
                  maxWidth: '100%',
                  minWidth: 0,
                  boxSizing: 'border-box',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {a.name} · AI
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ minWidth: 0, maxWidth: '100%' }}>
        <div style={{ color: theme.textSecondary, fontSize: 12, marginBottom: 6 }}>
          Scope to an initiative (optional — reps answer in its context)
        </div>
        <select
          value={initiativeId}
          onChange={(e) => setInitiativeId(e.target.value)}
          style={{
            // Keep the CLOSED control within the panel width even when a long initiative name is
            // selected: a constrained native <select> truncates its shown value. (The OPEN option
            // list expanding wider is native browser behavior and is fine/desired.) min-width:0 is
            // essential so this control can shrink inside its flex parent on the full /chat page.
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            boxSizing: 'border-box',
            padding: '9px 12px',
            borderRadius: 6,
            border: `1px solid ${theme.border}`,
            backgroundColor: theme.background,
            color: theme.text,
            fontSize: 14,
          }}
        >
          <option value="">No initiative (general)</option>
          {initiatives.map((i) => (
            <option key={i.id} value={i.id}>
              {i.name || i.id}
            </option>
          ))}
        </select>
      </div>

      <div style={{ minWidth: 0, maxWidth: '100%' }}>
        <div style={{ color: theme.textSecondary, fontSize: 12, marginBottom: 6 }}>
          Scope to a Quest (optional, reps email this quest's people)
        </div>
        <select
          value={questId}
          onChange={(e) => setQuestId(e.target.value)}
          aria-label="Quest for the new conversation"
          style={{
            // Same width discipline as the initiative select above.
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            boxSizing: 'border-box',
            padding: '9px 12px',
            borderRadius: 6,
            border: `1px solid ${theme.border}`,
            backgroundColor: theme.background,
            color: theme.text,
            fontSize: 14,
          }}
        >
          <option value="">No quest</option>
          {quests.map((q) => (
            <option key={q.quest_id} value={q.quest_id}>
              {q.team_name}: {q.completed ? `✓ ${q.outcome}` : q.outcome}
            </option>
          ))}
        </select>
      </div>

      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Title (optional — auto-generated if blank)"
        style={{
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          padding: '9px 12px',
          borderRadius: 6,
          border: `1px solid ${theme.border}`,
          backgroundColor: theme.background,
          color: theme.text,
          fontSize: 14,
        }}
      />

      <div style={{ color: theme.textSecondary, fontSize: 11 }}>
        You'll post as <strong>{cap(creator)}</strong> (change in the navbar).
      </div>

      {error && <div style={{ color: '#ef4444', fontSize: 13 }}>{error}</div>}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', maxWidth: '100%' }}>
        <button
          onClick={create}
          disabled={creating || selectedAis.length === 0}
          style={{
            padding: '9px 18px',
            borderRadius: 6,
            border: 'none',
            backgroundColor: creating || selectedAis.length === 0 ? theme.border : theme.primary,
            color: '#fff',
            cursor: creating || selectedAis.length === 0 ? 'not-allowed' : 'pointer',
            fontSize: 14,
            fontWeight: 600,
            boxSizing: 'border-box',
          }}
        >
          {creating ? 'Creating…' : 'Start chat'}
        </button>
        <button
          onClick={onCancel}
          style={{
            padding: '9px 18px',
            borderRadius: 6,
            border: `1px solid ${theme.border}`,
            backgroundColor: 'transparent',
            color: theme.text,
            cursor: 'pointer',
            fontSize: 14,
            boxSizing: 'border-box',
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

// ---- Give feedback / Teach panel (inline) ----------------------------------

// A compact, single-textarea feedback panel anchored to an AI message. The human types a
// CRITIQUE (not a rewrite); on save the feedback is saved to the rep's memory AND an improved
// reply is regenerated. Constrained to fit inside the narrow drawer (width:100%, box-sizing).
const FeedbackPanel: React.FC<{
  rep: string;
  busy: boolean;
  onSave: (feedback: string) => void;
  onCancel: () => void;
}> = ({ rep, busy, onSave, onCancel }) => {
  const { theme } = useTheme();
  const [feedback, setFeedback] = useState('');
  return (
    <div
      style={{
        marginTop: 6,
        padding: 10,
        borderRadius: 8,
        border: `1px solid ${AI_COLOR}`,
        backgroundColor: theme.surface,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
      }}
    >
      <div style={{ color: theme.textSecondary, fontSize: 11 }}>
        What was wrong / what should it do better?
      </div>
      <textarea
        value={feedback}
        onChange={(e) => setFeedback(e.target.value)}
        rows={3}
        autoFocus
        placeholder={`Tell ${cap(rep)} what to improve — saved to its AI memory and used to regenerate a better reply.`}
        style={{
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          padding: '8px 10px',
          borderRadius: 6,
          border: `1px solid ${theme.border}`,
          backgroundColor: theme.background,
          color: theme.text,
          fontSize: 13,
          resize: 'vertical',
          fontFamily: 'inherit',
        }}
      />
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          onClick={() => onSave(feedback.trim())}
          disabled={busy || !feedback.trim()}
          style={{
            padding: '7px 14px',
            borderRadius: 6,
            border: 'none',
            backgroundColor: busy || !feedback.trim() ? theme.border : AI_COLOR,
            color: '#fff',
            cursor: busy || !feedback.trim() ? 'not-allowed' : 'pointer',
            fontSize: 13,
            fontWeight: 600,
            boxSizing: 'border-box',
          }}
        >
          {busy ? 'Saving…' : 'Save & Teach'}
        </button>
        <button
          onClick={onCancel}
          style={{
            padding: '7px 14px',
            borderRadius: 6,
            border: `1px solid ${theme.border}`,
            backgroundColor: 'transparent',
            color: theme.text,
            cursor: 'pointer',
            fontSize: 13,
            boxSizing: 'border-box',
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

// ---- Edit-your-own-message panel -------------------------------------------

// Inline editor for a message YOU sent, prefilled with the current text. Saving rewrites the
// message in place and regenerates the rep's reply (instead of deleting + re-sending). Styled
// like the other inline panels but with the human/primary accent, not the AI purple.
const EditPanel: React.FC<{
  initial: string;
  busy: boolean;
  onSave: (content: string) => void;
  onCancel: () => void;
}> = ({ initial, busy, onSave, onCancel }) => {
  const { theme } = useTheme();
  const [text, setText] = useState(initial);
  const changed = text.trim().length > 0 && text.trim() !== initial.trim();
  return (
    <div
      style={{
        marginTop: 6,
        padding: 10,
        borderRadius: 8,
        border: `1px solid ${theme.primary}`,
        backgroundColor: theme.surface,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
      }}
    >
      <div style={{ color: theme.textSecondary, fontSize: 11 }}>
        Edit your message. Saving updates it and regenerates the reply.
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={3}
        autoFocus
        // Cmd/Ctrl+Enter saves; Esc cancels (parity with how the composer feels).
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && changed && !busy) onSave(text.trim());
          if (e.key === 'Escape') onCancel();
        }}
        style={{
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          padding: '8px 10px',
          borderRadius: 6,
          border: `1px solid ${theme.border}`,
          backgroundColor: theme.background,
          color: theme.text,
          fontSize: 13,
          resize: 'vertical',
          fontFamily: 'inherit',
        }}
      />
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          onClick={() => onSave(text.trim())}
          disabled={busy || !changed}
          style={{
            padding: '7px 14px',
            borderRadius: 6,
            border: 'none',
            backgroundColor: busy || !changed ? theme.border : theme.primary,
            color: '#fff',
            cursor: busy || !changed ? 'not-allowed' : 'pointer',
            fontSize: 13,
            fontWeight: 600,
            boxSizing: 'border-box',
          }}
        >
          {busy ? 'Saving…' : 'Save & regenerate'}
        </button>
        <button
          onClick={onCancel}
          style={{
            padding: '7px 14px',
            borderRadius: 6,
            border: `1px solid ${theme.border}`,
            backgroundColor: 'transparent',
            color: theme.text,
            cursor: 'pointer',
            fontSize: 13,
            boxSizing: 'border-box',
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

// ---- Bring in the real person panel (modal overlay) ------------------------

// A centered modal, styled like the other chat surfaces (useTheme colors, AI-purple accents),
// that REPLACES the old browser-dialog flow. Picks a real person (pill buttons over
// IDENTITY_PEOPLE) and captures a one-line reason. Constrained to fit the narrow chat drawer.
const BringHumanPanel: React.FC<{
  defaultHuman: string;
  busy: boolean;
  onSend: (human: string, reason: string) => void;
  onCancel: () => void;
}> = ({ defaultHuman, busy, onSend, onCancel }) => {
  const { theme } = useTheme();
  // Default to the message's person if it's a known person, else the first rep.
  const initialHuman = IDENTITY_PEOPLE.includes(defaultHuman as any)
    ? defaultHuman
    : IDENTITY_PEOPLE[0];
  const [human, setHuman] = useState<string>(initialHuman);
  const [reason, setReason] = useState('');

  // Escape closes without sending.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div
      // Click-outside (on the dimmed overlay) closes without sending.
      onClick={onCancel}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 60,
        backgroundColor: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 12,
        boxSizing: 'border-box',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 'min(420px, 92vw)',
          boxSizing: 'border-box',
          backgroundColor: theme.surface,
          border: `1px solid ${AI_COLOR}`,
          borderRadius: 12,
          boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
          padding: 16,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <div style={{ color: theme.text, fontWeight: 700, fontSize: 15 }}>
          Bring in the real person
        </div>

        <div style={{ minWidth: 0, maxWidth: '100%' }}>
          <div style={{ color: theme.textSecondary, fontSize: 12, marginBottom: 6 }}>
            Who should step in?
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, maxWidth: '100%' }}>
            {IDENTITY_PEOPLE.map((p) => {
              const active = p === human;
              return (
                <button
                  key={p}
                  onClick={() => setHuman(p)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: 999,
                    border: `1px solid ${active ? AI_COLOR : theme.border}`,
                    backgroundColor: active ? AI_COLOR : theme.surface,
                    color: active ? '#fff' : theme.text,
                    cursor: 'pointer',
                    fontSize: 13,
                    maxWidth: '100%',
                    minWidth: 0,
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {personLabel(p)}
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ minWidth: 0, maxWidth: '100%' }}>
          <div style={{ color: theme.textSecondary, fontSize: 12, marginBottom: 6 }}>
            Why are they needed?
          </div>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            autoFocus
            placeholder="One-line reason they're needed"
            style={{
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              padding: '8px 10px',
              borderRadius: 6,
              border: `1px solid ${theme.border}`,
              backgroundColor: theme.background,
              color: theme.text,
              fontSize: 13,
              resize: 'vertical',
              fontFamily: 'inherit',
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            onClick={() => onSend(human, reason.trim())}
            disabled={busy || !human}
            style={{
              padding: '8px 16px',
              borderRadius: 6,
              border: 'none',
              backgroundColor: busy || !human ? theme.border : AI_COLOR,
              color: '#fff',
              cursor: busy || !human ? 'not-allowed' : 'pointer',
              fontSize: 13,
              fontWeight: 600,
              boxSizing: 'border-box',
            }}
          >
            {busy ? 'Sending…' : `Bring in ${cap(human)}`}
          </button>
          <button
            onClick={onCancel}
            style={{
              padding: '8px 16px',
              borderRadius: 6,
              border: `1px solid ${theme.border}`,
              backgroundColor: 'transparent',
              color: theme.text,
              cursor: 'pointer',
              fontSize: 13,
              boxSizing: 'border-box',
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// ---- A single message bubble (with AI actions) -----------------------------

// A version chain for one AI reply: the ROOT (original) followed by each successive revision
// produced by teaching, oldest -> newest. For non-AI / un-revised messages it's a single entry.
// The thread renders the LATEST version as the live reply, with earlier versions tucked behind a
// "Previous versions" expander. Teaching/endorsing/bringing-in act on the LATEST version.
const MessageBubble: React.FC<{
  versions: ConversationMessage[]; // oldest -> newest; the live reply is the last entry
  identity: string;
  aiRefs: string[];
  busyMsgId: number | null;
  onTeach: (m: ConversationMessage, feedback: string) => void;
  onEndorse: (m: ConversationMessage) => void;
  onBringHuman: (m: ConversationMessage) => void;
  // DEEP MODE: "have {rep} actually DO this" — spawn a real Claude Code run for this message.
  onRunDeep: (m: ConversationMessage) => void;
  // Delete this single message (works for both human and AI messages).
  onDelete: (m: ConversationMessage) => void;
  // Edit a message YOU sent: rewrite it in place and regenerate the rep's reply.
  onEdit: (m: ConversationMessage, content: string) => void;
  // Auto-escalate (light): when the human's request that prompted THIS quick reply reads as
  // "do work", show a subtle inline "🔧 Run this for real?" affordance on the reply.
  showRunForReal?: boolean;
  // QUOTE-REPLY: refer to this message in the next thing you send (clicking the bubble does it).
  onReply: (m: ConversationMessage) => void;
  // True while THIS message is the composer's current reply target (bubble shows a ring).
  isReplyTarget?: boolean;
  // Jump to the message a quote points at (scroll + flash it in the thread).
  onJumpToMessage: (id: number) => void;
  // Set while this message is the jump target, so it flashes when someone lands on it.
  flashing?: boolean;
  // Interactive checkboxes: toggle one `- [ ]`/`- [x]` line parsed out of this message.
  onToggleChecklistItem: (m: ConversationMessage, item: ChecklistItem, checked: boolean) => void;
}> = ({ versions, identity, aiRefs, busyMsgId, onTeach, onEndorse, onBringHuman, onRunDeep, onDelete, onEdit, showRunForReal, onReply, isReplyTarget, onJumpToMessage, flashing, onToggleChecklistItem }) => {
  const { theme } = useTheme();
  const { openTask } = useTaskModal();
  const [menuOpen, setMenuOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  // Per-message controls stay hidden at rest and reveal on hover, so they don't
  // pad every message with a persistent button row. (Menu/edit keep them shown.)
  const [hovered, setHovered] = useState(false);
  // Touch has no hover, so tapping the bubble toggles the actions. Either path reveals them.
  const [tapped, setTapped] = useState(false);
  const showActions = hovered || tapped;

  const live = versions[versions.length - 1]; // the latest version is the live reply
  const earlier = versions.slice(0, -1);       // older versions, hidden behind the expander
  const wasRevised = earlier.length > 0;

  const isAi = live.sender_kind === 'ai' || live.role === 'assistant';
  const isMine = live.sender_kind === 'human' && live.sender_ref === identity;
  const sender = live.sender_ref || (isAi ? 'ai' : 'human');
  const busy = busyMsgId === live.id;
  // The earlier message this one refers to (quote-reply), as sent from the server.
  const quoted = live.reply_to || null;

  return (
    <div
      style={{ display: 'flex', gap: 8, flexDirection: isMine ? 'row-reverse' : 'row' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Avatar refName={sender} isAi={isAi} />
      <div style={{ maxWidth: '78%', minWidth: 0 }}>
        {/* Attribution label: AI reps as "Zee · AI" (purple); humans by name (solid). */}
        <div
          style={{
            fontSize: 11,
            marginBottom: 2,
            textAlign: isMine ? 'right' : 'left',
            color: isAi ? AI_COLOR : theme.textSecondary,
            fontWeight: isAi ? 700 : 600,
          }}
        >
          {cap(sender)}
          {isAi ? ' · AI' : ''}
          {live.corrects_message_id ? ' · correction' : ''}
          {live.edited_at ? ' · edited' : ''}
        </div>

        {/* QUOTE-REPLY: the earlier message this one refers to, above the bubble. Click it to jump
            back to the original in the thread. */}
        {quoted && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onJumpToMessage(quoted.id);
            }}
            title="Go to the message this refers to"
            style={{
              display: 'block',
              width: '100%',
              textAlign: 'left',
              marginBottom: 4,
              padding: '4px 8px',
              borderRadius: 6,
              border: 'none',
              borderLeft: `3px solid ${isMine ? theme.primary : AI_COLOR}`,
              backgroundColor: theme.surface,
              color: theme.textSecondary,
              cursor: 'pointer',
              fontSize: 12,
              lineHeight: 1.4,
            }}
          >
            <span style={{ fontWeight: 700 }}>↩ {cap(quoted.sender_ref || 'message')}</span>
            <span
              style={{
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {quoted.preview}
            </span>
          </button>
        )}

        <div
          id={`chat-msg-${live.id}`}
          onClick={() => {
            setTapped((v) => !v);
            // Clicking a message REFERS to it: it becomes the composer's reply target (clicking
            // the same message again clears it). Skipped when the click just finished selecting
            // text, so copying out of a bubble doesn't hijack the composer.
            const selected = typeof window !== 'undefined' ? window.getSelection()?.toString() : '';
            if (!selected) onReply(live);
          }}
          style={{
            padding: '10px 14px',
            borderRadius: isMine ? '14px 14px 2px 14px' : '14px 14px 14px 2px',
            backgroundColor: isMine ? theme.primary : isAi ? AI_COLOR + '14' : theme.surface,
            color: isMine ? '#fff' : theme.text,
            border: isMine
              ? 'none'
              : isAi
              ? `1px solid ${AI_COLOR}55`
              : `1px solid ${theme.border}`,
            // The composer's current reply target (and a just-jumped-to message) is ringed, so it's
            // obvious which message the next send refers to.
            boxShadow: isReplyTarget || flashing ? `0 0 0 2px ${theme.primary}` : 'none',
            fontSize: 14,
            lineHeight: 1.5,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            cursor: 'pointer',
            // Taught messages STAY visible (not struck). Only legacy `corrected` strikes.
            textDecoration: live.corrected ? 'line-through' : 'none',
            opacity: live.corrected ? 0.55 : 1,
          }}
          title="Click to refer to this message in your next one"
        >
          <MessageText
            text={live.content}
            checklistItems={live.checklist_items}
            onToggleChecklistItem={
              live.checklist_items && live.checklist_items.length > 0
                ? (item, checked) => onToggleChecklistItem(live, item, checked)
                : undefined
            }
          />
        </div>

        {/* TRACKING: ANY rep message that names a cockpit task opens it in the SHARED task
            modal IN-PLACE, so the run is visible/reviewable without leaving the chat (the
            chat is the home base). This deliberately covers plain prose ("I have started
            task #2715", "Task #2710 is complete"), not just the server-posted
            "🔧 On it, running this as task #N" line. Gating on isDeepRepMessage used to hide
            the button on every ordinary sentence, which is why a task could be running and
            still look untrackable from the chat. Every id in the message gets its own button;
            if an id names a task that no longer exists the shared modal says "Task not
            found.", which is the honest answer and far more useful than no affordance. */}
        {isAi && (() => {
          const re = /task #(\d+)/gi;
          const text = live.content || '';
          const ids: number[] = [];
          let m: RegExpExecArray | null;
          // eslint-disable-next-line no-cond-assign
          while ((m = re.exec(text)) !== null) {
            const id = Number(m[1]);
            if (id > 0 && ids.indexOf(id) === -1) ids.push(id);
          }
          if (ids.length === 0) return null;
          return (
            <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {ids.map((taskId) => (
                <button
                  key={taskId}
                  type="button"
                  onClick={() => openTask(taskId)}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    color: AI_COLOR,
                    fontSize: 12,
                    fontWeight: 600,
                    textDecoration: 'none',
                    cursor: 'pointer',
                  }}
                  title="Open the tracked cockpit task (live output + history)"
                >
                  🔧 Open task #{taskId} ↗
                </button>
              ))}
            </div>
          );
        })()}

        {/* Sent timestamp (day + time) for this bubble. For a version chain this is the LATEST
            version's time; earlier versions' times show in the "Previous versions" expander. */}
        {fmtTimestamp(live.created_at) && (
          <div
            style={{
              fontSize: 10,
              marginTop: 3,
              color: theme.textSecondary,
              textAlign: isMine ? 'right' : 'left',
            }}
            title={live.created_at || undefined}
          >
            {fmtTimestamp(live.created_at)}
          </div>
        )}

        {/* Tags row: revised / taught / corrected / endorsed (reflecting the LIVE reply). */}
        <div style={{ display: 'flex', gap: 6, marginTop: 3, flexWrap: 'wrap', justifyContent: isMine ? 'flex-end' : 'flex-start' }}>
          {wasRevised && (
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: AI_COLOR,
                backgroundColor: AI_COLOR + '18',
                border: `1px solid ${AI_COLOR}55`,
                borderRadius: 999,
                padding: '1px 7px',
                textTransform: 'uppercase',
              }}
              title="This reply was regenerated from feedback. The reply it improved is shown as its own message above."
            >
              revised
            </span>
          )}
          {/* A still-un-revised reply that got feedback shows a "taught" badge. */}
          {!wasRevised && live.taught && (
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: AI_COLOR,
                backgroundColor: AI_COLOR + '18',
                border: `1px solid ${AI_COLOR}55`,
                borderRadius: 999,
                padding: '1px 7px',
                textTransform: 'uppercase',
              }}
              title="A human gave feedback on this reply; the rep learned from it."
            >
              taught
            </span>
          )}
          {live.corrected && (
            <span style={{ fontSize: 10, fontWeight: 700, color: theme.warning, textTransform: 'uppercase' }}>
              corrected
            </span>
          )}
          {live.endorsed_by && (
            <span style={{ fontSize: 11, fontWeight: 700, color: theme.success }}>
              ✓ endorsed by {cap(live.endorsed_by)}
            </span>
          )}
        </div>

        {/* If the live reply itself (the latest version) carries feedback not yet acted on, show it. */}
        {!wasRevised && live.taught && live.taught_feedback && (
          <div
            style={{
              marginTop: 3,
              fontSize: 11,
              fontStyle: 'italic',
              color: theme.textSecondary,
              maxWidth: '100%',
              wordBreak: 'break-word',
              textAlign: isMine ? 'right' : 'left',
            }}
          >
            taught: {live.taught_feedback}
          </div>
        )}

        {/* Previous versions expander (legacy): only renders if a slot ever carries more than one
            version. Each message is now its own bubble, so `earlier` is empty and this is inert —
            kept defensively so any future grouped slot still degrades gracefully. */}
        {earlier.length > 0 && (
          <div style={{ marginTop: 4, textAlign: isMine ? 'right' : 'left' }}>
            <button
              onClick={() => setShowHistory((v) => !v)}
              style={{
                background: 'none',
                border: 'none',
                color: theme.textSecondary,
                cursor: 'pointer',
                fontSize: 12,
                padding: 0,
              }}
              title="Show earlier versions of this reply and the feedback that improved it"
            >
              Previous versions ({earlier.length}) {showHistory ? '▴' : '▾'}
            </button>
            {showHistory && (
              <div
                style={{
                  marginTop: 6,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                  borderLeft: `2px solid ${AI_COLOR}33`,
                  paddingLeft: 10,
                }}
              >
                {earlier.map((v) => (
                  <div key={v.id} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <div
                      style={{
                        padding: '8px 12px',
                        borderRadius: '12px 12px 12px 2px',
                        backgroundColor: theme.surface,
                        color: theme.textSecondary,
                        border: `1px solid ${theme.border}`,
                        fontSize: 13,
                        lineHeight: 1.45,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      <MessageText text={v.content} />
                    </div>
                    {fmtTimestamp(v.created_at) && (
                      <div style={{ fontSize: 10, color: theme.textSecondary }} title={v.created_at || undefined}>
                        {fmtTimestamp(v.created_at)}
                      </div>
                    )}
                    {/* The feedback on THIS version is what produced the next version. */}
                    {v.taught_feedback && (
                      <div
                        style={{
                          fontSize: 11,
                          fontStyle: 'italic',
                          color: theme.textSecondary,
                          wordBreak: 'break-word',
                        }}
                      >
                        {cap(identity)} taught: {v.taught_feedback}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* AI message actions (act on the LIVE / latest version). The trigger only
            appears on hover (or while its menu is open) so it adds no resting height. */}
        {isAi && !live.corrected && (showActions || menuOpen || editing || showRunForReal) && (
          <div style={{ marginTop: 4 }}>
            {!editing && (showActions || menuOpen) && (
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <button
                  onClick={() => setMenuOpen((v) => !v)}
                  style={{
                    background: 'none',
                    border: `1px solid ${theme.border}`,
                    borderRadius: 6,
                    color: theme.textSecondary,
                    cursor: 'pointer',
                    fontSize: 12,
                    padding: '2px 8px',
                  }}
                  title="Actions on this AI message"
                >
                  ··· actions
                </button>
                {menuOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      marginTop: 4,
                      backgroundColor: theme.surface,
                      border: `1px solid ${theme.border}`,
                      borderRadius: 8,
                      boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
                      zIndex: 20,
                      minWidth: 190,
                      overflow: 'hidden',
                    }}
                  >
                    {[
                      { label: '↩ Reply to this', fn: () => { onReply(live); setMenuOpen(false); } },
                      { label: '✎ Give feedback / Teach', fn: () => { setEditing(true); setMenuOpen(false); } },
                      { label: '✓ Endorse', fn: () => { onEndorse(live); setMenuOpen(false); } },
                      {
                        label: `🔧 Have ${cap(live.sender_ref || aiRefs[0] || 'rep')} actually do this`,
                        fn: () => { onRunDeep(live); setMenuOpen(false); },
                      },
                      {
                        label: `🙋 Bring in the real ${cap(live.sender_ref || aiRefs[0] || 'human')}`,
                        fn: () => { onBringHuman(live); setMenuOpen(false); },
                      },
                      {
                        label: '🗑️ Delete message',
                        fn: () => { onDelete(live); setMenuOpen(false); },
                      },
                    ].map((item) => (
                      <button
                        key={item.label}
                        onClick={item.fn}
                        disabled={busy}
                        style={{
                          display: 'block',
                          width: '100%',
                          textAlign: 'left',
                          padding: '9px 12px',
                          background: 'none',
                          border: 'none',
                          borderBottom: `1px solid ${theme.border}`,
                          color: theme.text,
                          cursor: busy ? 'not-allowed' : 'pointer',
                          fontSize: 13,
                        }}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {editing && (
              <FeedbackPanel
                rep={live.sender_ref || aiRefs[0] || 'rep'}
                busy={busy}
                onSave={(feedback) => {
                  onTeach(live, feedback);
                  setEditing(false);
                }}
                onCancel={() => setEditing(false)}
              />
            )}
            {/* Auto-escalate (light): non-intrusive nudge to run the originating request for real.
                Never auto-spawns — it's a single click that calls onRunDeep. */}
            {showRunForReal && !editing && (
              <button
                onClick={() => onRunDeep(live)}
                disabled={busy}
                title={`Have ${cap(live.sender_ref || aiRefs[0] || 'rep')} actually do this as a tracked task`}
                style={{
                  marginLeft: 8,
                  background: 'none',
                  border: `1px dashed ${AI_COLOR}88`,
                  borderRadius: 6,
                  color: AI_COLOR,
                  cursor: busy ? 'not-allowed' : 'pointer',
                  fontSize: 12,
                  padding: '2px 8px',
                }}
              >
                🔧 Run this for real?
              </button>
            )}
          </div>
        )}

        {/* Edit-your-own-message panel (only for messages YOU sent). Replaces the action row
            while open. Saving rewrites the message in place and regenerates the rep's reply. */}
        {!isAi && isMine && editing && (
          <EditPanel
            initial={live.content}
            busy={busy}
            onSave={(content) => {
              onEdit(live, content);
              setEditing(false);
            }}
            onCancel={() => setEditing(false)}
          />
        )}

        {/* Action controls for HUMAN messages (AI messages get their own ··· menu).
            Hover-only, so they don't pad every human message with a resting button row.
            Edit is offered only on YOUR OWN messages; Delete on any human message. */}
        {!isAi && !editing && showActions && (
          <div
            style={{
              marginTop: 4,
              textAlign: isMine ? 'right' : 'left',
              display: 'flex',
              gap: 6,
              justifyContent: isMine ? 'flex-end' : 'flex-start',
            }}
          >
            <button
              onClick={() => onReply(live)}
              title="Refer to this message in your next one"
              aria-label="Reply to this message"
              style={{
                background: isReplyTarget ? theme.primary + '22' : 'none',
                border: `1px solid ${isReplyTarget ? theme.primary : theme.border}`,
                borderRadius: 6,
                color: theme.textSecondary,
                cursor: 'pointer',
                fontSize: 12,
                padding: '2px 8px',
              }}
            >
              ↩ Reply
            </button>
            {isMine && (
              <button
                onClick={() => setEditing(true)}
                disabled={busy}
                title="Edit this message and regenerate the reply"
                aria-label="Edit this message"
                style={{
                  background: 'none',
                  border: `1px solid ${theme.border}`,
                  borderRadius: 6,
                  color: theme.textSecondary,
                  cursor: busy ? 'not-allowed' : 'pointer',
                  fontSize: 12,
                  padding: '2px 8px',
                }}
              >
                ✎ Edit
              </button>
            )}
            <button
              onClick={() => onDelete(live)}
              disabled={busy}
              title="Delete this message"
              aria-label="Delete this message"
              style={{
                background: 'none',
                border: `1px solid ${theme.border}`,
                borderRadius: 6,
                color: theme.textSecondary,
                cursor: busy ? 'not-allowed' : 'pointer',
                fontSize: 12,
                padding: '2px 8px',
              }}
            >
              🗑️ Delete
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ---- Thread ----------------------------------------------------------------

const Thread: React.FC<{
  conv: ConversationDetail | null;
  loading: boolean;
  sending: boolean;
  pendingReps: string[]; // reps with a live "typing…" placeholder (streaming, one per still-pending rep)
  // EPHEMERAL orchestrator status (the rep's current step: planning/reading/answering) or null.
  orchStatus: { rep: string; text: string } | null;
  // Confirm flow: the human answers the latest rep confirm question (true=go ahead, false=hold off).
  onConfirm: (approve: boolean) => void;
  identity: string;
  aiRefs: string[];
  busyMsgId: number | null;
  onTeach: (m: ConversationMessage, feedback: string) => void;
  onEndorse: (m: ConversationMessage) => void;
  onBringHuman: (m: ConversationMessage) => void;
  onRunDeep: (m: ConversationMessage) => void; // DEEP MODE: spawn a real run for a message
  onDelete: (m: ConversationMessage) => void;  // delete a single message (human or AI)
  onEdit: (m: ConversationMessage, content: string) => void; // edit your own message + regenerate
  // Dead-end recovery: when the LAST message is a human turn with no AI reply after it, this
  // fires the "⟳ Reps didn't reply — Retry" affordance. `retrying` shows the working state.
  onRetry: () => void;
  retrying: boolean;
  // QUOTE-REPLY: clicking a message makes it the composer's reply target (click again to clear).
  onReply: (m: ConversationMessage) => void;
  replyToId: number | null;
  // Interactive checkboxes: toggle one `- [ ]`/`- [x]` line parsed out of a message.
  onToggleChecklistItem: (m: ConversationMessage, item: ChecklistItem, checked: boolean) => void;
}> = ({ conv, loading, sending, pendingReps, orchStatus, onConfirm, identity, aiRefs, busyMsgId, onTeach, onEndorse, onBringHuman, onRunDeep, onDelete, onEdit, onRetry, retrying, onReply, replyToId, onToggleChecklistItem }) => {
  const { theme } = useTheme();
  const scrollRef = useRef<HTMLDivElement>(null);
  // The message a quote was just clicked through to: scrolled into view and ringed briefly.
  const [flashId, setFlashId] = useState<number | null>(null);

  const jumpToMessage = useCallback((id: number) => {
    const node = document.getElementById(`chat-msg-${id}`);
    if (!node) return;
    node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setFlashId(id);
    setTimeout(() => setFlashId((cur) => (cur === id ? null : cur)), 2000);
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [conv?.messages, sending, pendingReps, orchStatus]);

  // Confirm flow: the rep is awaiting confirmation when the LAST message is an AI reply that is
  // NOT a deep task-link/result message (those start with 🔧/✅/⚠️) and reads as a question. We
  // keep it simple: the last AI message, not a deep message, ending with "?" → show the buttons.
  const awaitingConfirm = useMemo(() => {
    const msgs = conv?.messages ?? [];
    if (msgs.length === 0) return false;
    const last = msgs[msgs.length - 1];
    const isAi = last.sender_kind === 'ai' || last.role === 'assistant';
    if (!isAi) return false;
    if (isDeepRepMessage(last.content)) return false;
    return (last.content || '').trimEnd().endsWith('?');
  }, [conv?.messages]);

  // Build VERSION CHAINS. A revised reply (revises_message_id) supersedes the message it points
  // at; we attach it to that message's chain and render the chain as ONE slot at the ROOT's
  // chronological position, showing the LATEST version as the live reply. Revised messages are
  // therefore NOT rendered again at their own (later) spot — they live inside the root's slot.
  //
  // Cross-rep guard: a message only folds into another's chain when the revision target has the
  // SAME sender_ref. If revises_message_id points to a different rep's message (which caused
  // that rep's slot to display the incoming rep's text), the message is treated as its own root.
  // NOTE: declared before any early return so the hook order is stable (rules-of-hooks).
  const chains = useMemo(() => {
    const msgs = conv?.messages ?? [];
    const byId = new Map<number, ConversationMessage>();
    for (const m of msgs) byId.set(m.id, m);

    // Resolve the chain ROOT for any message: walk revises_message_id back to a message that
    // doesn't itself revise anything (guard against cycles / missing parents / cross-rep links).
    const rootOf = (m: ConversationMessage): number => {
      let cur = m;
      const seen = new Set<number>();
      while (cur.revises_message_id != null && byId.has(cur.revises_message_id) && !seen.has(cur.id)) {
        const parent = byId.get(cur.revises_message_id)!;
        // Only follow the link when the same sender wrote both (prevent cross-rep overwriting).
        if (parent.sender_ref !== cur.sender_ref) break;
        seen.add(cur.id);
        cur = parent;
      }
      return cur.id;
    };

    // Group messages by their chain root, preserving each chain's chronological (id) order.
    const versionsByRoot = new Map<number, ConversationMessage[]>();
    for (const m of msgs) {
      const rid = rootOf(m);
      const arr = versionsByRoot.get(rid) || [];
      arr.push(m);
      versionsByRoot.set(rid, arr);
    }
    versionsByRoot.forEach((arr) => arr.sort((a, b) => a.id - b.id));

    // Emit ONE slot per chain, at the chronological position of its ROOT. A same-rep revision is
    // skipped at its own position (shown inside the root's slot). Cross-rep revisions keep their
    // own root slot.
    return msgs
      .filter((m) => {
        if (m.revises_message_id == null) return true;
        const parent = byId.get(m.revises_message_id);
        if (!parent) return true;
        if (parent.sender_ref !== m.sender_ref) return true; // cross-rep: own bubble
        return false;
      })
      .map((root) => versionsByRoot.get(root.id) || [root]);
  }, [conv?.messages]);

  // DEAD-END detection: the conversation's LAST message is a HUMAN turn with no AI message after
  // it (e.g. the reply got rolled back by a server reload). When so — and nothing is currently
  // sending/streaming — we surface the subtle "⟳ Reps didn't reply — Retry" affordance below the
  // thread. NOTE: declared before any early return so the hook order stays stable.
  const isDeadEnd = useMemo(() => {
    const msgs = conv?.messages ?? [];
    if (msgs.length === 0) return false;
    const last = msgs[msgs.length - 1];
    return last.sender_kind === 'human' || (last.sender_kind == null && last.role === 'user');
  }, [conv?.messages]);

  if (loading) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.textSecondary }}>
        Loading conversation…
      </div>
    );
  }
  if (!conv) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.textSecondary, padding: 24, textAlign: 'center' }}>
        Pick a conversation, or start a new one.
      </div>
    );
  }

  return (
    <div
      ref={scrollRef}
      style={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10, padding: 14 }}
    >
      {conv.messages.length === 0 ? (
        <div style={{ color: theme.textSecondary, fontSize: 14, textAlign: 'center', padding: 16 }}>
          Say hello to start the conversation.
        </div>
      ) : (
        chains.map((versions) => {
          const live = versions[versions.length - 1];
          const isAi = live.sender_kind === 'ai' || live.role === 'assistant';
          // Auto-escalate affordance: show on a normal (non-deep) AI reply when the human turn
          // that immediately precedes the ROOT of this chain reads as a "do work" request.
          let showRunForReal = false;
          if (isAi && !isDeepRepMessage(live.content)) {
            const all = conv.messages;
            const rootIdx = all.findIndex((m) => m.id === versions[0].id);
            for (let i = rootIdx - 1; i >= 0; i--) {
              const prev = all[i];
              if (prev.sender_kind === 'human' || prev.role === 'user') {
                showRunForReal = looksLikeWork(prev.content);
                break;
              }
            }
          }
          return (
            <MessageBubble
              key={versions[0].id}
              versions={versions}
              identity={identity}
              aiRefs={aiRefs}
              busyMsgId={busyMsgId}
              onTeach={onTeach}
              onEndorse={onEndorse}
              onBringHuman={onBringHuman}
              onRunDeep={onRunDeep}
              onDelete={onDelete}
              onEdit={onEdit}
              showRunForReal={showRunForReal}
              onReply={onReply}
              isReplyTarget={replyToId === live.id}
              onJumpToMessage={jumpToMessage}
              flashing={flashId === live.id}
              onToggleChecklistItem={onToggleChecklistItem}
            />
          );
        })
      )}
      {/* ORCHESTRATOR: reasoning log rendered as a real message in the feed while the rep is
          working. Styled identically to a persisted AI message so it flows naturally in the
          conversation. Clears on reply/confirm/done — never persisted. */}
      {orchStatus && (
        <div style={{ display: 'flex', gap: 8 }}>
          <Avatar refName={orchStatus.rep} isAi />
          <div style={{ maxWidth: '78%', minWidth: 0 }}>
            <div style={{ fontSize: 11, marginBottom: 2, color: AI_COLOR, fontWeight: 700 }}>
              {cap(orchStatus.rep)} · AI
            </div>
            <div
              style={{
                padding: '10px 14px',
                borderRadius: '14px 14px 14px 2px',
                backgroundColor: AI_COLOR + '14',
                color: theme.text,
                border: `1px solid ${AI_COLOR}55`,
                fontSize: 14,
                lineHeight: 1.5,
                wordBreak: 'break-word',
                whiteSpace: 'pre-line',
              }}
            >
              <MessageText text={orchStatus.text} />
              <span className="chatdock-typing-dots" style={{ color: theme.textSecondary }}>…</span>
            </div>
          </div>
        </div>
      )}
      {/* Streaming: one "{Rep} · AI is typing…" placeholder per rep still generating. */}
      {pendingReps.map((rep) => (
        <TypingPlaceholder key={`pending-${rep}`} rep={rep} />
      ))}
      {/* CONFIRM flow: when the last AI message is a question awaiting confirmation, offer
          Confirm / Decline. Clicking either sends a normal follow-up so the next turn proceeds. */}
      {awaitingConfirm && !sending && !orchStatus && (
        <div style={{ display: 'flex', justifyContent: 'flex-start', gap: 8, marginLeft: 36 }}>
          <button
            type="button"
            onClick={() => onConfirm(true)}
            style={{
              background: AI_COLOR,
              border: 'none',
              borderRadius: 999,
              color: '#fff',
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: 600,
              padding: '4px 14px',
            }}
            title="Tell the rep to go ahead"
          >
            ✓ Confirm
          </button>
          <button
            type="button"
            onClick={() => onConfirm(false)}
            style={{
              background: 'none',
              border: `1px solid ${theme.border}`,
              borderRadius: 999,
              color: theme.textSecondary,
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: 600,
              padding: '4px 14px',
            }}
            title="Tell the rep to hold off"
          >
            ✕ Decline
          </button>
        </div>
      )}
      {/* Fallback (non-stream send path): a single generic typing bubble while waiting. */}
      {sending && pendingReps.length === 0 && (
        <div style={{ display: 'flex', gap: 8 }}>
          <Avatar refName="ai" isAi />
          <div
            style={{
              padding: '10px 14px',
              borderRadius: '14px 14px 14px 2px',
              backgroundColor: AI_COLOR + '14',
              color: theme.textSecondary,
              border: `1px solid ${AI_COLOR}55`,
              fontSize: 14,
              fontStyle: 'italic',
            }}
          >
            typing…
          </div>
        </div>
      )}
      {/* DEAD-END recovery: last message is a human turn with no AI reply and nothing is in
          flight — offer a subtle one-click retry to regenerate the reps' replies. */}
      {isDeadEnd && !sending && pendingReps.length === 0 && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 2 }}>
          <button
            type="button"
            onClick={onRetry}
            disabled={retrying}
            title="The reps didn't reply to your last message — regenerate their reply."
            style={{
              background: 'none',
              border: `1px dashed ${theme.border}`,
              borderRadius: 999,
              color: theme.textSecondary,
              cursor: retrying ? 'wait' : 'pointer',
              fontSize: 12,
              padding: '3px 12px',
            }}
          >
            {retrying ? '⟳ Retrying…' : '⟳ Reps didn’t reply — Retry'}
          </button>
        </div>
      )}
    </div>
  );
};

// ---- ChatComposer ----------------------------------------------------------
// Owns all input/mention state locally so that keystrokes only re-render this
// small component, not the entire ChatDockInner (which includes the full Thread).

interface ChatComposerProps {
  identity: string;
  sending: boolean;
  mentionCandidates: Array<{ ref: string; name: string }>;
  onSend: (text: string) => void;
  // QUOTE-REPLY: the message the next send refers to (clicked in the thread), or null.
  replyTo: ConversationMessage | null;
  onClearReply: () => void;
}

const MENTION_AT_CARET_RE = /(?:^|\s)@([a-zA-Z0-9_-]*)$/;

const ChatComposer: React.FC<ChatComposerProps> = React.memo(
  ({ identity, sending, mentionCandidates, onSend, replyTo, onClearReply }) => {
    const { theme } = useTheme();
    const [input, setInput] = useState('');
    const [mentionOpen, setMentionOpen] = useState(false);
    const [mentionQuery, setMentionQuery] = useState('');
    const [mentionIndex, setMentionIndex] = useState(0);
    const composerRef = useRef<HTMLTextAreaElement>(null);

    // Picking a message to refer to should put the cursor where you type the follow-up.
    useEffect(() => {
      if (replyTo) composerRef.current?.focus();
    }, [replyTo]);

    const filteredMentions = useMemo(() => {
      if (!mentionOpen) return [];
      const q = mentionQuery.toLowerCase();
      if (!q) return mentionCandidates;
      return mentionCandidates.filter(
        (c) => c.ref.toLowerCase().startsWith(q) || c.name.toLowerCase().startsWith(q)
      );
    }, [mentionOpen, mentionQuery, mentionCandidates]);

    const syncMentionState = (value: string, caret: number) => {
      const before = value.slice(0, caret);
      const m = before.match(MENTION_AT_CARET_RE);
      if (m) {
        setMentionOpen(true);
        setMentionQuery(m[1]);
        setMentionIndex(0);
      } else if (mentionOpen) {
        setMentionOpen(false);
      }
    };

    const insertMention = (ref: string) => {
      const el = composerRef.current;
      const caret = el ? el.selectionStart ?? input.length : input.length;
      const before = input.slice(0, caret);
      const after = input.slice(caret);
      const newBefore = before.replace(MENTION_AT_CARET_RE, (full) => {
        const lead = full.startsWith('@') ? '' : full[0];
        return `${lead}@${ref} `;
      });
      setInput(newBefore + after);
      setMentionOpen(false);
      const newCaret = newBefore.length;
      setTimeout(() => {
        const node = composerRef.current;
        if (node) {
          node.focus();
          node.setSelectionRange(newCaret, newCaret);
        }
      }, 0);
    };

    const send = () => {
      const text = input.trim();
      if (!text || sending) return;
      setInput('');
      setMentionOpen(false);
      onSend(text);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (mentionOpen && filteredMentions.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setMentionIndex((i) => (i + 1) % filteredMentions.length);
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setMentionIndex((i) => (i - 1 + filteredMentions.length) % filteredMentions.length);
          return;
        }
        if (e.key === 'Enter' || e.key === 'Tab') {
          e.preventDefault();
          insertMention(filteredMentions[Math.min(mentionIndex, filteredMentions.length - 1)].ref);
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          setMentionOpen(false);
          return;
        }
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        send();
      }
    };

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {/* QUOTE-REPLY chip: what the next message refers to, with an ✕ to drop the reference. */}
      {replyTo && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '5px 8px',
            borderRadius: 6,
            borderLeft: `3px solid ${theme.primary}`,
            backgroundColor: theme.surface,
            fontSize: 12,
            color: theme.textSecondary,
          }}
        >
          <span style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
            ↩ Replying to {cap(replyTo.sender_ref || 'message')}
          </span>
          <span
            style={{
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={replyTo.content}
          >
            {replyTo.content}
          </span>
          <button
            type="button"
            onClick={onClearReply}
            title="Don't refer to that message"
            aria-label="Clear the replied-to message"
            style={{
              background: 'none',
              border: 'none',
              color: theme.textSecondary,
              cursor: 'pointer',
              fontSize: 14,
              lineHeight: 1,
              padding: 2,
            }}
          >
            ✕
          </button>
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
        <div style={{ flex: 1, position: 'relative', minWidth: 0 }}>
          {mentionOpen && filteredMentions.length > 0 && (
            <div
              style={{
                position: 'absolute',
                bottom: '100%',
                left: 0,
                marginBottom: 6,
                zIndex: 40,
                minWidth: 200,
                maxWidth: '100%',
                maxHeight: 220,
                overflowY: 'auto',
                backgroundColor: theme.surface,
                border: `1px solid ${theme.border}`,
                borderRadius: 8,
                boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
              }}
            >
              <div
                style={{
                  padding: '6px 10px',
                  fontSize: 11,
                  fontWeight: 700,
                  color: theme.textSecondary,
                  borderBottom: `1px solid ${theme.border}`,
                }}
              >
                Mention a rep
              </div>
              {filteredMentions.map((c, i) => {
                const active = i === Math.min(mentionIndex, filteredMentions.length - 1);
                return (
                  <button
                    key={c.ref}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      insertMention(c.ref);
                    }}
                    onMouseEnter={() => setMentionIndex(i)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      width: '100%',
                      textAlign: 'left',
                      padding: '8px 10px',
                      background: active ? AI_COLOR + '18' : 'none',
                      border: 'none',
                      borderBottom: `1px solid ${theme.border}`,
                      color: theme.text,
                      cursor: 'pointer',
                      fontSize: 13,
                    }}
                  >
                    <Avatar refName={c.ref} isAi />
                    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.name} · AI <span style={{ color: theme.textSecondary }}>@{c.ref}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}
          <textarea
            ref={composerRef}
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              syncMentionState(e.target.value, e.target.selectionStart ?? e.target.value.length);
            }}
            onKeyDown={handleKeyDown}
            onClick={(e) =>
              syncMentionState(e.currentTarget.value, e.currentTarget.selectionStart ?? 0)
            }
            onBlur={() => setMentionOpen(false)}
            placeholder={`Message as ${cap(identity)}… (Enter to send, Shift+Enter for newline)`}
            rows={2}
            style={{
              width: '100%',
              boxSizing: 'border-box',
              padding: '9px 12px',
              borderRadius: 6,
              border: `1px solid ${theme.border}`,
              backgroundColor: theme.background,
              color: theme.text,
              fontSize: 14,
              resize: 'vertical',
              fontFamily: 'inherit',
            }}
          />
        </div>
        <button
          onClick={send}
          disabled={sending || !input.trim()}
          title="Send message"
          style={{
            padding: '10px 18px',
            borderRadius: 6,
            border: 'none',
            backgroundColor: sending || !input.trim() ? theme.border : theme.primary,
            color: '#fff',
            cursor: sending || !input.trim() ? 'not-allowed' : 'pointer',
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          {sending ? '…' : 'Send'}
        </button>
      </div>
      </div>
    );
  }
);

// ---- The shared inner dock -------------------------------------------------

export interface ChatDockInnerProps {
  /** 'drawer' is compact; 'page' is roomier. Purely presentational. */
  variant: 'drawer' | 'page';
  /** Drawer shows an "expand ↗" link to /chat and a close button. */
  onExpand?: () => void;
  onClose?: () => void;
}

const ChatDockInner: React.FC<ChatDockInnerProps> = ({ variant, onExpand, onClose }) => {
  const { theme } = useTheme();
  const { currentPerson } = useIdentity();
  const identity = currentPerson;
  const {
    pendingDirectRep,
    clearPendingDirectRep,
    pendingConversationId,
    clearPendingConversation,
    requestOpenConversation,
  } = useChat();

  const [agents, setAgents] = useState<ChatAgent[]>([]);
  const [initiatives, setInitiatives] = useState<InitiativeSummary[]>([]);
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [activeConv, setActiveConv] = useState<ConversationDetail | null>(null);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingConv, setLoadingConv] = useState(false);
  const [sending, setSending] = useState(false);
  // Selectable Claude models for the per-conversation model picker (loaded once, best-effort).
  const [availableModels, setAvailableModels] = useState<string[]>([]);
  // Reps with a live "typing…" placeholder during a streaming send (removed as each rep replies).
  const [pendingReps, setPendingReps] = useState<string[]>([]);
  // EPHEMERAL orchestrator status: the rep's current step ("planning…", "reading 2 files…",
  // "answering"), shown as a transient indicator and cleared when the reply/confirm/done lands.
  // Never persisted — purely a live UI affordance during the orchestrated send.
  const [orchStatus, setOrchStatus] = useState<{ rep: string; text: string } | null>(null);
  const [showNew, setShowNew] = useState(false);
  // True while a deep-linked DIRECT chat ("Chat with X's AI") is being found-or-created and
  // loaded. Forces the thread pane open immediately (never the picker / bare list) so the
  // one-click flow lands straight in the conversation, showing a "Opening chat…" state.
  const [directLoading, setDirectLoading] = useState(false);
  const [listCollapsed, setListCollapsed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initiativeFilter, setInitiativeFilter] = useState<string>(''); // '' = All
  const [busyMsgId, setBusyMsgId] = useState<number | null>(null);
  // True while the dead-end "⟳ Retry" recovery (regenerate the missing reply) is in flight.
  const [retrying, setRetrying] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  // The AI message we're bringing a real person in for; non-null opens the in-app panel.
  const [bringHumanFor, setBringHumanFor] = useState<ConversationMessage | null>(null);
  // QUOTE-REPLY: the earlier message the next send refers to (set by clicking a message in the
  // thread). Cleared on send, on clicking the same message again, and on switching conversations.
  const [replyTo, setReplyTo] = useState<ConversationMessage | null>(null);
  // "Edit reps" popover (active-conversation header): open/closed + the rep ref currently being
  // added/removed (disables that row while the request is in flight).
  const [editRepsOpen, setEditRepsOpen] = useState(false);
  const [repBusy, setRepBusy] = useState<string | null>(null);
  // True while a model change is being saved, to disable the picker briefly.
  const [modelSaving, setModelSaving] = useState(false);
  // Quest picker: every quest the cockpit can see (loaded once, best-effort) and whether a
  // quest change is currently saving. Scoping a chat to a quest tells its reps which quest's
  // people to mail, so Zee never has to paste a quest id.
  const [quests, setQuests] = useState<QuestOption[]>([]);
  const [questSaving, setQuestSaving] = useState(false);

  // Load the selectable models once (best-effort; the picker just falls back to default if this fails).
  useEffect(() => {
    let cancelled = false;
    getAvailableModels()
      .then((models) => {
        if (!cancelled) setAvailableModels(Array.isArray(models) ? models : []);
      })
      .catch(() => {
        /* model picker is optional; ignore load failure */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Load the selectable quests once (best-effort, 5-minute backend cache). If the Teams API is
  // unreachable the list is simply empty and the picker shows "No quest".
  useEffect(() => {
    let cancelled = false;
    listQuests()
      .then((qs) => {
        if (!cancelled) setQuests(Array.isArray(qs) ? qs : []);
      })
      .catch(() => {
        /* quest picker is optional; ignore load failure */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Change (or clear) the active conversation's default model. Persists via the API and updates
  // both the open conversation and its list row so the choice sticks. Applies to every task the
  // conversation spawns; chat replies are unaffected.
  const handleModelChange = useCallback(
    async (model: string) => {
      if (!activeConv) return;
      const convId = activeConv.id;
      setModelSaving(true);
      // Optimistic: reflect the choice immediately.
      setActiveConv((prev) => (prev && prev.id === convId ? { ...prev, model: model || null } : prev));
      try {
        const updated = await updateConversationModel(convId, model);
        setActiveConv((prev) => (prev && prev.id === convId ? { ...prev, model: updated.model } : prev));
        setConversations((prev) =>
          prev.map((c) => (c.id === convId ? { ...c, model: updated.model } : c))
        );
      } catch {
        setError('Failed to update the conversation model.');
      } finally {
        setModelSaving(false);
      }
    },
    [activeConv]
  );

  // Scope (or unscope) the active conversation to a Quest. Same optimistic pattern as
  // handleModelChange. Once scoped, every rep in this chat is told the quest's outcome, team
  // and id, so "email the team about this" goes through that quest's own mailer.
  const handleQuestChange = useCallback(
    async (questId: string) => {
      if (!activeConv) return;
      const convId = activeConv.id;
      const previous = activeConv.quest_id ?? null;
      setQuestSaving(true);
      setActiveConv((prev) => (prev && prev.id === convId ? { ...prev, quest_id: questId || null } : prev));
      try {
        const updated = await setConversationQuest(convId, questId);
        setActiveConv((prev) =>
          prev && prev.id === convId ? { ...prev, quest_id: updated.quest_id } : prev
        );
        setConversations((prev) =>
          prev.map((c) => (c.id === convId ? { ...c, quest_id: updated.quest_id } : c))
        );
      } catch {
        setActiveConv((prev) => (prev && prev.id === convId ? { ...prev, quest_id: previous } : prev));
        setError("Failed to set this conversation's quest.");
      } finally {
        setQuestSaving(false);
      }
    },
    [activeConv]
  );

  // Quests grouped by team for the picker, preserving the backend's order (open quests first,
  // then team name, then outcome).
  const questGroups = useMemo(() => {
    const groups: { team: string; quests: QuestOption[] }[] = [];
    quests.forEach((q) => {
      const team = q.team_name || 'Team';
      const last = groups[groups.length - 1];
      if (last && last.team === team) last.quests.push(q);
      else groups.push({ team, quests: [q] });
    });
    return groups;
  }, [quests]);

  // Pin/unpin a conversation from its list row. Optimistic (same pattern as handleModelChange):
  // flip it in `conversations` immediately, PATCH the backend, then reconcile; revert + surface
  // an error if the call fails. Re-sorting to put pinned rows first happens on the next
  // refreshList (the backend already returns them pinned-first); we also re-sort locally here so
  // the row jumps to the top right away instead of waiting on a refetch.
  const handleTogglePin = useCallback(async (conv: ConversationSummary, pinned: boolean) => {
    const resort = (list: ConversationSummary[]) =>
      [...list].sort((a, b) => Number(b.pinned) - Number(a.pinned));
    setConversations((prev) =>
      resort(prev.map((c) => (c.id === conv.id ? { ...c, pinned } : c)))
    );
    try {
      const updated = await setConversationPinned(conv.id, pinned);
      setConversations((prev) =>
        resort(prev.map((c) => (c.id === conv.id ? { ...c, pinned: updated.pinned } : c)))
      );
      setActiveConv((prev) => (prev && prev.id === conv.id ? { ...prev, pinned: updated.pinned } : prev));
    } catch {
      setConversations((prev) =>
        resort(prev.map((c) => (c.id === conv.id ? { ...c, pinned: !pinned } : c)))
      );
      setError("Couldn't update the pin, try again.");
    }
  }, []);

  const refreshList = useCallback(async () => {
    setLoadingList(true);
    try {
      const convs = await listConversations({ initiativeId: initiativeFilter || undefined });
      setConversations(convs);
      return convs;
    } catch {
      setError('Failed to load conversations.');
      return [];
    } finally {
      setLoadingList(false);
    }
  }, [initiativeFilter]);

  // `silent` (used by the window-focus catch-up below) skips the loading flag and error banner,
  // so a background refresh never blanks the thread to a "Loading conversation…" screen — it just
  // swaps in the fresh data once it arrives. A user-initiated open/switch always shows the loader.
  const loadConv = useCallback(async (id: number, opts?: { silent?: boolean }) => {
    const silent = opts?.silent ?? false;
    if (!silent) {
      setLoadingConv(true);
      // Close the edit-reps popover when (re)loading a conversation so it never lingers across
      // a switch (and reopens cleanly against the freshly-loaded participant list).
      setEditRepsOpen(false);
    }
    try {
      const conv = await getConversation(id);
      setActiveConv(conv);
      setActiveId(id);
    } catch {
      if (!silent) setError('Failed to load conversation.');
    } finally {
      if (!silent) setLoadingConv(false);
    }
  }, []);

  useEffect(() => {
    listChatAgents().then(setAgents).catch(() => setAgents([]));
    listInitiatives().then(setInitiatives).catch(() => setInitiatives([]));
  }, []);

  // A quote-reply belongs to the thread it was picked in, so switching conversations drops it.
  useEffect(() => {
    setReplyTo(null);
  }, [activeId]);

  useEffect(() => {
    refreshList();
  }, [refreshList]);

  // ONE-CLICK direct chat: honor a deep-link request to open a 1:1 with a specific rep. We
  // find-or-create the direct conversation and land straight in its THREAD — never the picker.
  // `directLoading` keeps the thread pane (not the list/picker) visible while this resolves, and
  // we clear `showNew` up front so a stale picker can't flash.
  //
  // Staleness is tracked with a monotonic request id, NOT a cleanup `cancelled` flag. We clear
  // `pendingDirectRep` below, but it's also a dependency of this effect — so clearing it makes
  // React re-run the effect (and run the previous run's cleanup) WHILE the open is still in
  // flight. A `cancelled` cleanup flag can't tell that spurious self-triggered re-run apart from
  // a real supersede/unmount, so it would cancel the legitimate open and leave `directLoading`
  // stuck true forever ("Loading conversation" with no composer). The reqId fixes that: the
  // spurious re-run early-returns (pendingDirectRep is now null) WITHOUT bumping reqId, so the
  // in-flight open still matches and always finishes and clears its loader. A genuine new click
  // bumps reqId, so any stale open bails.
  // AbortController for the currently in-flight streaming send. Aborted when the user switches
  // conversations or the component unmounts, so stale streams don't occupy HTTP connections and
  // block subsequent requests (browsers cap HTTP/1.1 connections per host at 6).
  const streamAbortRef = useRef<AbortController | null>(null);

  // Abort any live stream and free the connection when switching away from a conversation.
  useEffect(() => {
    return () => {
      streamAbortRef.current?.abort();
    };
  }, [activeId]);

  const directReqRef = useRef(0);
  useEffect(() => {
    if (!pendingDirectRep) return;
    const rep = pendingDirectRep;
    const reqId = ++directReqRef.current;
    setDirectLoading(true);
    setShowNew(false);
    setListCollapsed(true);
    // Clear the pending flag immediately so re-clicks (which re-set it) always re-trigger.
    clearPendingDirectRep();
    (async () => {
      try {
        // find-or-create: reuses the SAME direct conversation on repeat clicks (no duplicates).
        const conv = await openDirectConversation(identity, rep);
        if (directReqRef.current !== reqId) return; // superseded by a newer open
        await loadConv(conv.id);
        refreshList();
      } catch {
        if (directReqRef.current === reqId) setError('Could not open that chat.');
      } finally {
        if (directReqRef.current === reqId) setDirectLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDirectRep, identity]);

  // Honor a deep-link request to open a specific conversation (the "Needs you" grouping).
  useEffect(() => {
    if (pendingConversationId == null) return;
    const id = pendingConversationId;
    clearPendingConversation();
    setShowNew(false);
    loadConv(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingConversationId]);

  // Pull in deep-run results that landed while the tab was away: refresh the active
  // conversation on window focus (only when a conversation is open and we're idle).
  //
  // Two things used to make this feel like a multi-second freeze on a long-running thread:
  // (1) it always went through the loading state, replacing the whole pane with "Loading
  // conversation…" while a big conversation's full history re-fetched; (2) nothing stopped a
  // rapid run of focus events (alt-tabbing, a window manager double-firing focus) from re-issuing
  // that fetch over and over. Fixed with a silent background refresh (no loading flash — see
  // `loadConv`) plus a cooldown so the catch-up runs at most once per short window.
  const lastFocusRefreshRef = useRef(0);
  useEffect(() => {
    const FOCUS_REFRESH_COOLDOWN_MS = 15000;
    const onFocus = () => {
      if (activeId == null || sending) return;
      const now = Date.now();
      if (now - lastFocusRefreshRef.current < FOCUS_REFRESH_COOLDOWN_MS) return;
      lastFocusRefreshRef.current = now;
      loadConv(activeId, { silent: true }).catch(() => {});
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [activeId, sending, loadConv]);

  // Append a message to the active conversation (used as each streamed event arrives). Dedupes
  // by id so a re-appended message (or the optimistic human placeholder) can't double-render.
  const appendMessage = useCallback((msg: ConversationMessage) => {
    setActiveConv((prev) => {
      if (!prev || prev.id !== msg.conversation_id) return prev;
      if (prev.messages.some((m) => m.id === msg.id)) return prev;
      return { ...prev, messages: [...prev.messages, msg] };
    });
  }, []);

  // DEEP MODE: spawn a real Claude Code run for `text` as a tracked cockpit Task. The rep's
  // "running as task #N 🔧" link message is posted server-side; we refresh the thread to pull
  // it in (and a few seconds later to pull in the result message when the run finishes). When
  // `rep` is omitted, the backend picks (direct -> sole rep; group -> @mention/first).
  const runDeepWork = useCallback(
    async (convId: number, text: string, rep?: string) => {
      setError(null);
      try {
        const res = await runDeep(convId, identity, text, rep);
        // Pull in the human turn + the rep's "running as task #N" link message immediately.
        await loadConv(convId);
        refreshList();
        setToast(`🔧 Running as task #${res.task_id} — it'll report back here when done.`);
        // Poll once more shortly after to surface the result message once the run completes.
        // (A light v1 refresh; the run can take longer, in which case a later focus/refresh
        // or the user reopening the thread will show the result.)
        window.setTimeout(() => {
          loadConv(convId).catch(() => {});
        }, 6000);
      } catch (e: any) {
        setError(e.response?.data?.detail || 'Failed to start the deep run.');
      }
    },
    [identity, loadConv, refreshList]
  );

  // Send `text` to a conversation as a normal message. Extracted so both the composer and the
  // Confirm/Decline buttons can fire a follow-up turn through the SAME orchestrated path.
  // `replyToMessageId` is the quote-reply target (the message clicked in the thread), or null.
  const sendText = async (convId: number, text: string, replyToMessageId?: number | null) => {
    setSending(true);
    setPendingReps([]);
    setOrchStatus(null);
    setError(null);

    // Optimistic human bubble (a negative temp id so it never collides with a real row; replaced
    // when the stream's `human` event delivers the persisted message).
    const optimisticId = -Date.now();
    setActiveConv((prev) => {
      if (!prev || prev.id !== convId) return prev;
      // Mirror the quote on the optimistic bubble so the reference is visible before the
      // server's `human` event lands.
      const target = replyToMessageId ? prev.messages.find((m) => m.id === replyToMessageId) : undefined;
      return {
        ...prev,
        messages: [
          ...prev.messages,
          {
            id: optimisticId,
            conversation_id: prev.id,
            role: 'user',
            sender_kind: 'human',
            sender_ref: identity,
            content: text,
            created_at: new Date().toISOString(),
            reply_to_message_id: target ? target.id : null,
            reply_to: target
              ? {
                  id: target.id,
                  sender_kind: target.sender_kind,
                  sender_ref: target.sender_ref,
                  preview: target.content,
                }
              : null,
          },
        ],
      };
    });

    const replaceOptimisticHuman = (msg: ConversationMessage) => {
      setActiveConv((prev) => {
        if (!prev || prev.id !== convId) return prev;
        const withoutOptimistic = prev.messages.filter((m) => m.id !== optimisticId);
        if (withoutOptimistic.some((m) => m.id === msg.id)) {
          return { ...prev, messages: withoutOptimistic };
        }
        return { ...prev, messages: [...withoutOptimistic, msg] };
      });
    };

    // ORCHESTRATED send (auto-deciding): the rep's planner decides per message what to do —
    // answer from grounded context / gather files / spawn a real task / ask to confirm. Each step
    // surfaces an ephemeral status; replies/confirm questions pop in as they land. Falls back to
    // the plain grounded streaming path (postChatMessageStream) if the orchestrator stream errors.
    streamAbortRef.current?.abort();
    const streamAbort = new AbortController();
    streamAbortRef.current = streamAbort;
    try {
      await orchestrateMessage(convId, identity, text, (event) => {
        if (event.type === 'human') {
          replaceOptimisticHuman(event.message);
        } else if (event.type === 'status') {
          setOrchStatus(prev => ({
            rep: event.rep,
            text: prev?.rep === event.rep ? `${prev.text}\n${event.text}` : event.text,
          }));
        } else if (event.type === 'context') {
          // Context assembly event: show selected cards + sources
          const { data } = event;
          const cardText = data?.card_metadata
            ?.map((c: any) => `  • [${c.adapter}] ${c.title} (score: ${(c.relevance_score || 0).toFixed(2)}, ${c.file_count} files)`)
            .join('\n') || '';
          const contextStatus = `Selected context:\n${cardText || '(no cards selected)'}`;
          setOrchStatus(prev => ({
            rep: event.rep,
            text: prev?.rep === event.rep ? `${prev.text}\n${contextStatus}` : contextStatus,
          }));
        } else if (event.type === 'reply') {
          setOrchStatus(null);
          appendMessage(event.message);
          // A reply carrying a task_id / deep_subtasks means this turn spun off a cockpit
          // task (assigned to the rep being chatted with). Tell the Task panel to refresh
          // so the new task shows up immediately instead of only after a manual reload.
          if (event.task_id || (event.deep_subtasks && event.deep_subtasks.length > 0)) {
            notifyTaskModalChanged();
          }
        } else if (event.type === 'confirm') {
          // The rep's question is persisted + appended; the Confirm/Decline buttons render off it.
          setOrchStatus(null);
          appendMessage(event.message);
        } else if (event.type === 'done') {
          setOrchStatus(null);
        } else if (event.type === 'error') {
          throw new Error(event.detail || 'orchestrate error');
        }
      }, activeConv?.model || undefined, streamAbort.signal, replyToMessageId);
      setOrchStatus(null);
      setPendingReps([]);
      refreshList();
    } catch (orchErr: any) {
      // User switching conversations aborts the stream — not an error worth surfacing.
      if (orchErr?.name === 'AbortError') { setSending(false); return; }
      // Fallback 1: the orchestrator stream failed — use the plain grounded streaming path so the
      // message still gets a reply and the thread stays consistent.
      setOrchStatus(null);
      try {
        await postChatMessageStream(convId, identity, text, (event) => {
          if (event.type === 'human') {
            replaceOptimisticHuman(event.message);
          } else if (event.type === 'pending') {
            setPendingReps(event.reps || []);
          } else if (event.type === 'context') {
            // Context assembly event (fallback path): show selected cards + sources
            const { data } = event;
            const cardText = data?.card_metadata
              ?.map((c: any) => `  • [${c.adapter}] ${c.title} (score: ${(c.relevance_score || 0).toFixed(2)}, ${c.file_count} files)`)
              .join('\n') || '';
            setPendingReps((reps) => reps); // keep current state
            // Note: status display in fallback path is limited; context appears via data
          } else if (event.type === 'reply') {
            const rep = event.message.sender_ref;
            if (rep) setPendingReps((reps) => reps.filter((r) => r !== rep));
            appendMessage(event.message);
          } else if (event.type === 'done') {
            setPendingReps([]);
            // A task spun off from this message → refresh the Task panel.
            if (event.created_task_id) notifyTaskModalChanged();
          } else if (event.type === 'error') {
            throw new Error(event.detail || 'stream error');
          }
        }, undefined, streamAbort.signal, replyToMessageId);
        setPendingReps([]);
        refreshList();
      } catch (e2: any) {
        if (e2?.name === 'AbortError') { setSending(false); return; }
        // Fallback 2: even streaming failed — use the non-stream batch path.
        setPendingReps([]);
        try {
          const res = await postChatMessage(convId, identity, text, undefined, replyToMessageId);
          await loadConv(convId);
          refreshList();
          if (res?.created_task_id) notifyTaskModalChanged();
        } catch (e3: any) {
          setError(e3.response?.data?.detail || 'Failed to send. Please try again.');
        }
      }
    } finally {
      setSending(false);
      setPendingReps([]);
      setOrchStatus(null);
    }
  };

  // Called by ChatComposer when the user hits Send. Guards on activeId (can't send without a conv).
  const handleSend = useCallback(
    async (text: string) => {
      if (!activeId) return;
      // Consume the quote-reply target with this send (the reference belongs to one message).
      const quotedId = replyTo?.id && replyTo.id > 0 ? replyTo.id : null;
      setReplyTo(null);
      await sendText(activeId, text, quotedId);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeId, replyTo]
  );

  // Clicking a message in the thread makes it what the next message refers to; clicking the same
  // one again clears the reference. Optimistic (negative-id) bubbles aren't referable yet.
  const handleReplyTo = useCallback((m: ConversationMessage) => {
    if (m.id <= 0) return;
    setReplyTo((cur) => (cur && cur.id === m.id ? null : m));
  }, []);

  const clearReplyTo = useCallback(() => setReplyTo(null), []);

  // Confirm flow: the human answers a rep's confirm question. Confirm → "Yes, go ahead" lets the
  // next turn proceed; Decline → "No, hold off". Both are normal follow-up messages (orchestrated).
  const answerConfirm = async (approve: boolean) => {
    if (!activeId || sending) return;
    await sendText(activeId, approve ? 'Yes, go ahead.' : 'No, hold off.');
  };

  const aiRefs = useMemo(
    () => (activeConv ? activeConv.participants.filter((p) => p.kind === 'ai').map((p) => p.ref) : []),
    [activeConv]
  );
  // The reps that can be @mentioned in this conversation (passed to ChatComposer).
  const mentionCandidates = useMemo(
    () =>
      aiRefs.map((ref) => ({
        ref,
        name: agents.find((a) => a.ref === ref)?.name || cap(ref),
      })),
    [aiRefs, agents]
  );

  // DEEP MODE per-message + auto-escalate action: "have {rep} actually do this". We run the
  // request that prompted message `m` (its nearest preceding human turn; falls back to m's own
  // content if none) for real, assigned to m's rep. Spawns a tracked cockpit Task.
  const handleRunDeepForMessage = async (m: ConversationMessage) => {
    if (!activeId || !activeConv) return;
    const rep = m.sender_ref || aiRefs[0] || undefined;
    let requestText = m.content;
    const idx = activeConv.messages.findIndex((x) => x.id === m.id);
    for (let i = idx - 1; i >= 0; i--) {
      const prev = activeConv.messages[i];
      if (prev.sender_kind === 'human' || prev.role === 'user') {
        requestText = prev.content;
        break;
      }
    }
    // CONFIRM BEFORE SPAWNING. This path posts the request again and creates a tracked task with
    // no planner gate of any kind, so a stray or repeated click is a real task in someone's lane
    // (two identical tasks were created seconds apart this way). Show what will actually run, so
    // the click is a decision rather than a surprise.
    const preview = requestText.trim().replace(/\s+/g, ' ').slice(0, 200);
    const ok = window.confirm(
      `Run this as a real task for ${cap(rep || 'the rep')}?\n\n"${preview}${requestText.trim().length > 200 ? '…' : ''}"\n\n` +
      'It will run in the background and report back in this chat.'
    );
    if (!ok) return;
    setBusyMsgId(m.id);
    try {
      await runDeepWork(activeId, requestText, rep);
    } finally {
      setBusyMsgId(null);
    }
  };

  // ---- collaboration actions ----------------------------------------------
  // Feedback-first: the human critiques an AI reply. The feedback is saved to the rep's AI
  // memory AND an improved reply is regenerated, linked to the original via revises_message_id.
  // Reloading rebuilds the version chain so the improved reply appears IN PLACE of the original
  // (same slot in the thread) with earlier versions behind a "Previous versions" expander.
  const handleTeach = async (m: ConversationMessage, feedback: string) => {
    if (!activeId || !feedback) return;
    setBusyMsgId(m.id);
    setError(null);
    try {
      const res = await teachMessage(activeId, m.id, {
        teacher: identity,
        feedback,
      });
      // Reload so the version chain rebuilds: the improved reply renders in the original's slot.
      await loadConv(activeId);
      refreshList();
      // If the feedback read as "actually DO it", the rep started a real task → refresh Tasks.
      if (res.created_task_id) notifyTaskModalChanged();
      const memPart = res.saved_to_memory
        ? `✓ Saved to ${cap(res.rep)}'s AI memory`
        : `Feedback noted`;
      const base = res.revised_message ? memPart : `${memPart} — couldn't regenerate a reply`;
      setToast(res.created_task_id ? `${base} · started task #${res.created_task_id}` : base);
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to save the feedback.');
    } finally {
      setBusyMsgId(null);
    }
  };

  const handleEndorse = async (m: ConversationMessage) => {
    if (!activeId) return;
    setBusyMsgId(m.id);
    setError(null);
    try {
      const res = await endorseMessage(activeId, m.id, identity);
      await loadConv(activeId);
      setToast(
        res.saved_to_memory
          ? `✓ Endorsed — saved as a good example to ${cap(res.rep)}'s memory`
          : `✓ Endorsed by ${cap(identity)}`
      );
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to endorse.');
    } finally {
      setBusyMsgId(null);
    }
  };

  // Toggle one `- [ ]`/`- [x]` checkbox rendered out of a message. Optimistic (same pattern as
  // handleModelChange above): flip it in activeConv immediately so the click feels instant, PATCH
  // the backend, then reconcile with its response; revert + surface an error if the call fails.
  //
  // Checking a box for the FIRST time also starts it as a real background task server-side; the
  // PATCH response carries the new `task_id`, which we merge into local state so the checkbox
  // reflects it on re-render. The rep's "Checked off. Running as task #N." announcement is a
  // separate chat message posted server-side, so we reload the thread to pull it in (same
  // pattern as runDeepWork above) — it already renders with a clickable "Open task #N" button.
  const handleToggleChecklistItem = useCallback(
    async (m: ConversationMessage, item: ChecklistItem, checked: boolean) => {
      const convId = m.conversation_id;
      const applyItem = (value: Partial<ChecklistItem>) =>
        setActiveConv((prev) => {
          if (!prev || prev.id !== convId) return prev;
          return {
            ...prev,
            messages: prev.messages.map((msg) =>
              msg.id !== m.id || !msg.checklist_items
                ? msg
                : {
                    ...msg,
                    checklist_items: msg.checklist_items.map((it) =>
                      it.id === item.id ? { ...it, ...value } : it
                    ),
                  }
            ),
          };
        });
      applyItem({ checked });
      try {
        const updated = await setChecklistItemChecked(convId, m.id, item.id, checked);
        applyItem({ checked: updated.checked, task_id: updated.task_id });
        if (updated.task_id && !item.task_id) {
          // Newly spawned — pull in the rep's "Running as task #N" announcement.
          await loadConv(convId);
          refreshList();
        }
      } catch {
        applyItem({ checked: !checked }); // revert
        setError("Couldn't save that checkbox, try again.");
      }
    },
    [loadConv, refreshList]
  );

  // DEAD-END recovery: the loaded conversation ends on a human turn with no AI reply (e.g. the
  // reply was rolled back by a server reload). Regenerate the reps' replies for that last human
  // turn, then reload so they appear. Guarded against double-fire and against running mid-send.
  const handleRetryReply = async () => {
    if (!activeId || retrying || sending) return;
    setRetrying(true);
    setError(null);
    try {
      const res = await retryReply(activeId);
      await loadConv(activeId);
      refreshList();
      if (!res.messages || res.messages.length === 0) {
        setToast('No reply to regenerate.');
      }
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to regenerate the reply.');
    } finally {
      setRetrying(false);
    }
  };

  // Open the in-app panel (no browser dialogs); the actual request runs in submitBringHuman.
  const handleBringHuman = (m: ConversationMessage) => {
    setBringHumanFor(m);
  };

  const submitBringHuman = async (human: string, reason: string) => {
    const m = bringHumanFor;
    if (!activeId || !m || !human) return;
    setBusyMsgId(m.id);
    setError(null);
    try {
      await requestHuman(activeId, { human, reason, by: identity });
      await loadConv(activeId);
      refreshList();
      setToast(`⏳ Waiting on ${cap(human)}`);
      setBringHumanFor(null);
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to bring in a human.');
    } finally {
      setBusyMsgId(null);
    }
  };

  // ---- delete actions ------------------------------------------------------
  // Delete a whole conversation (trash control in the list). Confirms, deletes, refreshes the
  // list, and clears the thread view if the deleted conversation was the active one.
  const handleDeleteConversation = async (conv: ConversationSummary) => {
    if (!window.confirm(`Delete the conversation "${conv.title}" and all its messages? This can't be undone.`)) {
      return;
    }
    setError(null);
    try {
      await deleteConversation(conv.id);
      if (activeId === conv.id) {
        setActiveConv(null);
        setActiveId(null);
      }
      await refreshList();
      setToast('🗑️ Conversation deleted');
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to delete the conversation.');
    }
  };

  // Delete a single message (human or AI) from the active thread. Confirms, deletes, then
  // removes it from the thread optimistically (and refreshes the list's last-message preview).
  const handleDeleteMessage = async (m: ConversationMessage) => {
    if (!activeId) return;
    if (!window.confirm('Delete this message? This can\'t be undone.')) return;
    setBusyMsgId(m.id);
    setError(null);
    try {
      await deleteMessage(activeId, m.id);
      setActiveConv((prev) =>
        prev && prev.id === activeId
          ? { ...prev, messages: prev.messages.filter((x) => x.id !== m.id) }
          : prev
      );
      refreshList();
      setToast('🗑️ Message deleted');
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to delete the message.');
    } finally {
      setBusyMsgId(null);
    }
  };

  // Edit a message YOU sent, then regenerate the rep's reply against the new text (instead of
  // deleting + re-typing). Optimistically rewrites the message in place and drops the now-stale
  // tail (the old reply + any later turns), then streams the regenerated reply through the SAME
  // orchestrated path a normal send uses. On any failure, reloads the thread to resync.
  const handleEditMessage = async (m: ConversationMessage, content: string) => {
    const convId = activeId;
    const text = (content || '').trim();
    if (!convId || !text || text === (m.content || '').trim()) return;

    setBusyMsgId(m.id);
    setSending(true);
    setPendingReps([]);
    setOrchStatus(null);
    setError(null);

    // Optimistic: rewrite this message in place + truncate everything after it (stale once edited).
    setActiveConv((prev) => {
      if (!prev || prev.id !== convId) return prev;
      const idx = prev.messages.findIndex((x) => x.id === m.id);
      if (idx < 0) return prev;
      const kept = prev.messages.slice(0, idx + 1);
      kept[idx] = { ...kept[idx], content: text, edited_at: new Date().toISOString() };
      return { ...prev, messages: kept };
    });

    const replaceById = (msg: ConversationMessage) => {
      setActiveConv((prev) => {
        if (!prev || prev.id !== convId) return prev;
        const exists = prev.messages.some((x) => x.id === msg.id);
        const messages = exists
          ? prev.messages.map((x) => (x.id === msg.id ? msg : x))
          : [...prev.messages, msg];
        return { ...prev, messages };
      });
    };

    streamAbortRef.current?.abort();
    const editAbort = new AbortController();
    streamAbortRef.current = editAbort;
    try {
      await editMessageStream(convId, m.id, identity, text, (event) => {
        if (event.type === 'human') {
          replaceById(event.message); // the edited message, as persisted (carries the same id)
        } else if (event.type === 'status') {
          setOrchStatus(prev => ({
            rep: event.rep,
            text: prev?.rep === event.rep ? `${prev.text}\n${event.text}` : event.text,
          }));
        } else if (event.type === 'context') {
          const { data } = event;
          const cardText = data?.card_metadata
            ?.map((c: any) => `  • [${c.adapter}] ${c.title} (score: ${(c.relevance_score || 0).toFixed(2)}, ${c.file_count} files)`)
            .join('\n') || '';
          const editContextStatus = `Selected context:\n${cardText || '(no cards selected)'}`;
          setOrchStatus(prev => ({
            rep: event.rep,
            text: prev?.rep === event.rep ? `${prev.text}\n${editContextStatus}` : editContextStatus,
          }));
        } else if (event.type === 'reply') {
          setOrchStatus(null);
          appendMessage(event.message);
          if (event.task_id || (event.deep_subtasks && event.deep_subtasks.length > 0)) {
            notifyTaskModalChanged();
          }
        } else if (event.type === 'confirm') {
          setOrchStatus(null);
          appendMessage(event.message);
        } else if (event.type === 'done') {
          setOrchStatus(null);
        } else if (event.type === 'error') {
          throw new Error(event.detail || 'edit error');
        }
      }, activeConv?.model || undefined, editAbort.signal);
      refreshList();
    } catch (e: any) {
      if (e?.name === 'AbortError') return;
      // Stream failed after the edit committed server-side: reload so the thread reflects the
      // persisted state (edited message + whatever reply did land) instead of a half-applied UI.
      setError(e?.response?.data?.detail || 'Saved your edit, but the reply could not be regenerated. Reloading…');
      await loadConv(convId).catch(() => {});
      refreshList();
    } finally {
      setBusyMsgId(null);
      setSending(false);
      setPendingReps([]);
      setOrchStatus(null);
    }
  };

  // ---- edit reps (add/remove which AIs are in this conversation, any time) --
  // Toggle a rep in/out of the active conversation. Adding -> addParticipant; removing ->
  // removeParticipant (the backend refuses removing the LAST rep). After either, reload the
  // conversation so avatars + the participant line + kind (direct/group) update live, and refresh
  // the list (its kind chip / preview). The last remaining rep is guarded in the UI too.
  const handleToggleRep = async (ref: string, currentlyIn: boolean) => {
    if (!activeId) return;
    // Guard the last rep: never remove it (a chat needs ≥1 rep). UI also disables this row.
    if (currentlyIn && aiRefs.length <= 1) {
      setError('A chat needs at least one rep.');
      return;
    }
    setRepBusy(ref);
    setError(null);
    try {
      if (currentlyIn) {
        await removeConversationParticipant(activeId, ref);
        setToast(`Removed ${cap(ref)} from this chat`);
      } else {
        await addConversationParticipant(activeId, ref);
        setToast(`Added ${cap(ref)} to this chat`);
      }
      await loadConv(activeId);
      refreshList();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Could not update the reps.');
    } finally {
      setRepBusy(null);
    }
  };

  // ---- list pane -----------------------------------------------------------
  const listPane = (
    <div
      style={{
        width: variant === 'page' ? 320 : '100%',
        flex: variant === 'page' ? '0 0 320px' : undefined,
        borderRight: variant === 'page' ? `1px solid ${theme.border}` : undefined,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <div style={{ padding: 12, borderBottom: `1px solid ${theme.border}`, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: theme.textSecondary, fontSize: 12 }}>You are</span>
          <span
            style={{
              padding: '4px 10px',
              borderRadius: 999,
              backgroundColor: theme.primary,
              color: '#fff',
              fontSize: 12,
              fontWeight: 700,
            }}
            title="Set in the navbar (one global identity)"
          >
            {cap(identity)}
          </span>
          <span style={{ color: theme.textSecondary, fontSize: 11 }}>(set in navbar)</span>
        </div>

        {/* Initiative filter: All or a specific initiative (all its chats over time). Hidden while
            the New Chat panel is open so the initiative is only selectable in ONE place at a time —
            there, the new-chat "Scope to an initiative" selector is the single control. */}
        {!showNew && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ color: theme.textSecondary, fontSize: 12 }}>Initiative</span>
            <select
              value={initiativeFilter}
              onChange={(e) => setInitiativeFilter(e.target.value)}
              style={{
                flex: 1,
                minWidth: 0,
                maxWidth: '100%',
                boxSizing: 'border-box',
                padding: '6px 8px',
                borderRadius: 6,
                border: `1px solid ${theme.border}`,
                backgroundColor: theme.surface,
                color: theme.text,
                fontSize: 13,
              }}
            >
              <option value="">All</option>
              {initiatives.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.name || i.id}
                </option>
              ))}
            </select>
          </div>
        )}

        <button
          onClick={() => {
            setShowNew(true);
            setActiveConv(null);
            setActiveId(null);
          }}
          style={{
            padding: '8px 12px',
            borderRadius: 6,
            border: 'none',
            backgroundColor: theme.primary,
            color: '#fff',
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          + New chat
        </button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {loadingList ? (
          <div style={{ padding: 16, color: theme.textSecondary, fontSize: 13 }}>Loading…</div>
        ) : conversations.length === 0 ? (
          <div style={{ padding: 16, color: theme.textSecondary, fontSize: 13 }}>
            No conversations{initiativeFilter ? ' for this initiative' : ''} yet. Start one above.
          </div>
        ) : (
          // Pinned conversations are always grouped together at the very top (the backend
          // already returns them pinned-first; `conversations` is kept in that order locally
          // too — see handleTogglePin), with a small divider label, so a pinned chat never
          // requires scrolling to reach regardless of how many others exist below it.
          (() => {
            const pinnedConvs = conversations.filter((c) => c.pinned);
            const restConvs = conversations.filter((c) => !c.pinned);
            const renderRow = (c: ConversationSummary) => {
            const active = c.id === activeId;
            const initName = initiativeName(initiatives, c.initiative_id);
            return (
              <div
                key={c.id}
                style={{
                  position: 'relative',
                  borderBottom: `1px solid ${theme.border}`,
                  backgroundColor: active ? theme.surfaceHover : 'transparent',
                }}
              >
              {/* Pin control: keep this conversation reachable at the top without scrolling. */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleTogglePin(c, !c.pinned);
                }}
                title={c.pinned ? 'Unpin this conversation' : 'Pin this conversation to the top'}
                aria-label={c.pinned ? 'Unpin this conversation' : 'Pin this conversation'}
                style={{
                  position: 'absolute',
                  top: 6,
                  right: 32,
                  zIndex: 2,
                  background: c.pinned ? theme.surfaceHover : 'none',
                  border: c.pinned ? `1px solid ${AI_COLOR}` : '1px solid transparent',
                  borderRadius: 4,
                  color: c.pinned ? AI_COLOR : theme.textSecondary,
                  opacity: c.pinned ? 1 : 0.85,
                  cursor: 'pointer',
                  fontSize: 15,
                  padding: '2px 5px',
                  lineHeight: 1,
                }}
              >
                📌
              </button>
              {/* Trash control: delete this conversation (confirm). Overlaid so it isn't a button
                  nested inside the row button. */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteConversation(c);
                }}
                title="Delete this conversation"
                aria-label="Delete this conversation"
                style={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 2,
                  background: 'none',
                  border: 'none',
                  color: theme.textSecondary,
                  cursor: 'pointer',
                  fontSize: 13,
                  padding: '2px 4px',
                  lineHeight: 1,
                }}
              >
                🗑️
              </button>
              <button
                onClick={() => {
                  setShowNew(false);
                  loadConv(c.id);
                }}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  padding: '10px 54px 10px 12px',
                  border: 'none',
                  backgroundColor: 'transparent',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 3,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ color: theme.text, fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.pinned ? '📌 ' : ''}{c.title}
                  </span>
                  <span
                    style={{
                      flex: '0 0 auto',
                      fontSize: 10,
                      color: c.kind === 'group' ? AI_COLOR : theme.textSecondary,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                    }}
                  >
                    {c.kind}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  {initName && (
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: '#10b981',
                        backgroundColor: '#10b98118',
                        border: '1px solid #10b98155',
                        borderRadius: 999,
                        padding: '1px 7px',
                      }}
                    >
                      {initName}
                    </span>
                  )}
                  {c.awaiting_human && (
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: theme.warning,
                        backgroundColor: theme.warning + '18',
                        border: `1px solid ${theme.warning}`,
                        borderRadius: 999,
                        padding: '1px 7px',
                      }}
                    >
                      ⏳ {cap(c.awaiting_human)}
                    </span>
                  )}
                </div>
                <span style={{ color: theme.textSecondary, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {c.last_message ? c.last_message.preview : 'No messages yet'}
                </span>
              </button>
              </div>
            );
            };
            return (
              <>
                {pinnedConvs.length > 0 && (
                  <>
                    <div
                      style={{
                        padding: '6px 12px',
                        fontSize: 10,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: 0.4,
                        color: theme.textSecondary,
                      }}
                    >
                      📌 Pinned
                    </div>
                    {pinnedConvs.map(renderRow)}
                  </>
                )}
                {restConvs.map(renderRow)}
              </>
            );
          })()
        )}
      </div>
    </div>
  );

  // ---- main pane -----------------------------------------------------------
  // While a one-click direct chat is opening, treat the dock as having an active thread so the
  // list/picker never shows in its place.
  const showList = variant === 'page' || (!activeConv && !showNew && !directLoading) || !listCollapsed;
  const activeInitName = initiativeName(initiatives, activeConv?.initiative_id);

  const mainPane = (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0, position: 'relative' }}>
      {toast && <Toast msg={toast} onDone={() => setToast(null)} />}
      {bringHumanFor && (
        <BringHumanPanel
          defaultHuman={bringHumanFor.sender_ref || aiRefs[0] || ''}
          busy={busyMsgId === bringHumanFor.id}
          onSend={submitBringHuman}
          onCancel={() => setBringHumanFor(null)}
        />
      )}
      {showNew ? (
        <NewChatPanel
          quests={quests}
          agents={agents}
          initiatives={initiatives}
          creator={identity}
          defaultInitiativeId={initiativeFilter}
          onCreated={async (conv) => {
            setShowNew(false);
            await refreshList();
            loadConv(conv.id);
          }}
          onCancel={() => setShowNew(false)}
        />
      ) : (
        <>
          {activeConv && (
            <div
              style={{
                padding: '10px 14px',
                borderBottom: `1px solid ${theme.border}`,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {activeConv.participants
                  .filter((p) => p.kind === 'ai')
                  .map((p) => (
                    <span key={p.ref} style={{ marginLeft: -6 }}>
                      <Avatar refName={p.ref} isAi />
                    </span>
                  ))}
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ color: theme.text, fontSize: 14, fontWeight: 700 }}>{activeConv.title}</div>
                <div style={{ color: theme.textSecondary, fontSize: 11 }}>
                  {activeConv.participants.map((p) => `${cap(p.ref)} · AI`).join(', ')}
                </div>
              </div>
              {/* Quest scope: which quest this chat's work belongs to. Picking one tells every rep
                  in the chat the quest's outcome, team and id, so "email the team" goes out through
                  that quest's own mailer without anyone pasting an id (Joshua, 2026-09-15: "make it
                  easy for Zee by having a way to select the quest in the AI command center"). */}
              <select
                value={activeConv.quest_id || ''}
                onChange={(e) => handleQuestChange(e.target.value)}
                disabled={questSaving}
                title="Quest this conversation's work belongs to. Reps email this quest's people."
                aria-label="Quest for this conversation"
                style={{
                  flex: '0 0 auto',
                  maxWidth: 200,
                  background: theme.background,
                  border: `1px solid ${theme.border}`,
                  borderRadius: 6,
                  color: activeConv.quest_id ? theme.text : theme.textSecondary,
                  cursor: questSaving ? 'wait' : 'pointer',
                  fontSize: 12,
                  padding: '3px 6px',
                }}
              >
                <option value="">🎯 Quest: none</option>
                {questGroups.map((g) => (
                  <optgroup key={g.team} label={g.team}>
                    {g.quests.map((q) => (
                      <option key={q.quest_id} value={q.quest_id}>
                        {q.completed ? `✓ ${q.outcome}` : q.outcome}
                      </option>
                    ))}
                  </optgroup>
                ))}
                {/* Keep a saved-but-unlisted quest selectable (e.g. one this account can no longer see). */}
                {activeConv.quest_id && !quests.some((q) => q.quest_id === activeConv.quest_id) && (
                  <option value={activeConv.quest_id}>{activeConv.quest_id}</option>
                )}
              </select>
              {/* Per-conversation model: the default Claude model for EVERY task this conversation
                  spawns (chat replies stay on the system default). "System default" clears it. */}
              <select
                value={activeConv.model || ''}
                onChange={(e) => handleModelChange(e.target.value)}
                disabled={modelSaving}
                title="Default Claude model for tasks this conversation creates"
                aria-label="Conversation model for spawned tasks"
                style={{
                  flex: '0 0 auto',
                  maxWidth: 170,
                  background: theme.background,
                  border: `1px solid ${theme.border}`,
                  borderRadius: 6,
                  color: theme.textSecondary,
                  cursor: modelSaving ? 'wait' : 'pointer',
                  fontSize: 12,
                  padding: '3px 6px',
                }}
              >
                <option value="">⚙︎ Model: system default</option>
                {availableModels.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
                {/* Keep a saved-but-unlisted model visible (e.g. if the live list omits it). */}
                {activeConv.model && !availableModels.includes(activeConv.model) && (
                  <option value={activeConv.model}>{activeConv.model}</option>
                )}
              </select>
              {/* Edit reps: add/remove which AIs are in this conversation, any time. A compact
                  popover lists ALL reps with the in-conversation ones checked; toggling adds/removes
                  a participant live. The last remaining rep can't be unchecked (a chat needs ≥1). */}
              <div style={{ position: 'relative', flex: '0 0 auto' }}>
                <button
                  onClick={() => setEditRepsOpen((v) => !v)}
                  title="Add or remove which AI reps are in this conversation"
                  aria-label="Edit reps"
                  style={{
                    background: 'none',
                    border: `1px solid ${theme.border}`,
                    borderRadius: 6,
                    color: theme.textSecondary,
                    cursor: 'pointer',
                    fontSize: 12,
                    padding: '3px 8px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  ⚙️ Reps
                </button>
                {editRepsOpen && (
                  <>
                    {/* Click-catcher to close the popover when clicking elsewhere. */}
                    <div
                      onClick={() => setEditRepsOpen(false)}
                      style={{ position: 'fixed', inset: 0, zIndex: 30 }}
                    />
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        marginTop: 4,
                        zIndex: 31,
                        width: 220,
                        maxWidth: 'min(220px, 80vw)',
                        boxSizing: 'border-box',
                        backgroundColor: theme.surface,
                        border: `1px solid ${theme.border}`,
                        borderRadius: 8,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          padding: '8px 10px',
                          fontSize: 11,
                          fontWeight: 700,
                          color: theme.textSecondary,
                          borderBottom: `1px solid ${theme.border}`,
                        }}
                      >
                        Reps in this chat
                      </div>
                      <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                        {agents.map((a) => {
                          const inConv = aiRefs.includes(a.ref);
                          const isLast = inConv && aiRefs.length <= 1;
                          const rowBusy = repBusy === a.ref;
                          return (
                            <button
                              key={a.ref}
                              onClick={() => handleToggleRep(a.ref, inConv)}
                              disabled={rowBusy || isLast}
                              title={isLast ? 'A chat needs at least one rep' : undefined}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                width: '100%',
                                textAlign: 'left',
                                padding: '8px 10px',
                                background: 'none',
                                border: 'none',
                                borderBottom: `1px solid ${theme.border}`,
                                color: theme.text,
                                cursor: rowBusy || isLast ? 'not-allowed' : 'pointer',
                                opacity: isLast ? 0.6 : 1,
                                fontSize: 13,
                                boxSizing: 'border-box',
                              }}
                            >
                              <span
                                style={{
                                  flex: '0 0 auto',
                                  width: 16,
                                  height: 16,
                                  borderRadius: 4,
                                  border: `1px solid ${inConv ? AI_COLOR : theme.border}`,
                                  backgroundColor: inConv ? AI_COLOR : 'transparent',
                                  color: '#fff',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: 11,
                                  fontWeight: 700,
                                }}
                              >
                                {inConv ? '✓' : ''}
                              </span>
                              <span
                                style={{
                                  flex: 1,
                                  minWidth: 0,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {a.name} · AI
                              </span>
                              {rowBusy && (
                                <span style={{ flex: '0 0 auto', fontSize: 11, color: theme.textSecondary }}>…</span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                      <div style={{ padding: '6px 10px', fontSize: 10, color: theme.textSecondary }}>
                        A chat needs at least one rep.
                      </div>
                    </div>
                  </>
                )}
              </div>
              {/* Pin this open conversation. Same handler/state as the list-row pin button, so
                  toggling here keeps the list's "Pinned" grouping in sync immediately. Placed in
                  the open-conversation header (not just the list row) so it's visible the moment
                  you click into a chat, not just when scanning the unopened list. */}
              <button
                onClick={() => handleTogglePin(activeConv, !activeConv.pinned)}
                title={activeConv.pinned ? 'Unpin this conversation' : 'Pin this conversation to the top of the list'}
                aria-label={activeConv.pinned ? 'Unpin this conversation' : 'Pin this conversation'}
                style={{
                  flex: '0 0 auto',
                  background: activeConv.pinned ? theme.surfaceHover : 'none',
                  border: `1px solid ${activeConv.pinned ? AI_COLOR : theme.border}`,
                  borderRadius: 6,
                  color: activeConv.pinned ? AI_COLOR : theme.textSecondary,
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: activeConv.pinned ? 700 : 400,
                  padding: '3px 8px',
                  whiteSpace: 'nowrap',
                }}
              >
                📌 {activeConv.pinned ? 'Pinned' : 'Pin'}
              </button>
              {activeInitName && (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#10b981',
                    backgroundColor: '#10b98118',
                    border: '1px solid #10b98155',
                    borderRadius: 999,
                    padding: '2px 9px',
                  }}
                >
                  {activeInitName}
                </span>
              )}
              {activeConv.awaiting_human && (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: theme.warning,
                    backgroundColor: theme.warning + '18',
                    border: `1px solid ${theme.warning}`,
                    borderRadius: 999,
                    padding: '2px 9px',
                  }}
                  title="This conversation is waiting on a real person"
                >
                  ⏳ Waiting on {cap(activeConv.awaiting_human)}
                </span>
              )}
            </div>
          )}
          <Thread
            conv={activeConv}
            loading={loadingConv || directLoading}
            sending={sending}
            pendingReps={pendingReps}
            orchStatus={orchStatus}
            onConfirm={answerConfirm}
            identity={identity}
            aiRefs={aiRefs}
            busyMsgId={busyMsgId}
            onTeach={handleTeach}
            onEndorse={handleEndorse}
            onBringHuman={handleBringHuman}
            onRunDeep={handleRunDeepForMessage}
            onDelete={handleDeleteMessage}
            onEdit={handleEditMessage}
            onRetry={handleRetryReply}
            retrying={retrying}
            onReply={handleReplyTo}
            replyToId={replyTo?.id ?? null}
            onToggleChecklistItem={handleToggleChecklistItem}
          />
          {error && <div style={{ padding: '8px 14px', color: '#ef4444', fontSize: 13 }}>{error}</div>}
          {activeConv && (
            <div style={{ borderTop: `1px solid ${theme.border}`, padding: 10 }}>
              <ChatComposer
                identity={identity}
                sending={sending}
                mentionCandidates={mentionCandidates}
                onSend={handleSend}
                replyTo={replyTo}
                onClearReply={clearReplyTo}
              />
            </div>
          )}
        </>
      )}
    </div>
  );

  // Drawer header (mode controls).
  const header = (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px 12px',
        borderBottom: `1px solid ${theme.border}`,
        backgroundColor: theme.surface,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ color: theme.text, fontWeight: 700, fontSize: 15 }}>💬 Chat</span>
        {variant === 'drawer' && (activeConv || showNew) && (
          <button
            onClick={() => setListCollapsed((v) => !v)}
            style={{ background: 'none', border: 'none', color: theme.primary, cursor: 'pointer', fontSize: 12 }}
          >
            {listCollapsed ? 'Show list' : 'Hide list'}
          </button>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {variant === 'drawer' && onExpand && (
          <Link
            to="/chat"
            onClick={() => {
              // Carry the conversation the user is currently in over to the full /chat page so it
              // opens already showing this thread (instead of the bare list). The page variant
              // consumes pendingConversationId on mount via the same deep-link effect used by
              // "Needs you". Setting it momentarily flips drawerOpen true, but onExpand
              // (closeDrawer, below) flips it back false in the same handler, so the drawer still
              // closes. The drawer's own pendingConversationId effect doesn't fire because this
              // commit unmounts the drawer; only the freshly-mounted page instance reads it.
              if (activeId != null) requestOpenConversation(activeId);
              onExpand?.();
            }}
            style={{ color: theme.primary, fontSize: 13, fontWeight: 600, textDecoration: 'none' }}
            title="Open full chat page"
          >
            Expand ↗
          </Link>
        )}
        {variant === 'drawer' && onClose && (
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: theme.textSecondary, cursor: 'pointer', fontSize: 18 }}
            title="Close"
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );

  if (variant === 'page') {
    return (
      <div
        style={{
          display: 'flex',
          height: '100%',
          minHeight: 0,
          // Clamp the two-pane row so a wide child (a long initiative name, a long message)
          // can't push the whole /chat view past its container to the right.
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          overflowX: 'hidden',
        }}
      >
        {listPane}
        {mainPane}
      </div>
    );
  }

  // Drawer layout: header on top; list stacks above the thread (collapsible).
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflowX: 'hidden' }}>
      {header}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0, overflowX: 'hidden' }}>
        {showList && (
          <div style={{ maxHeight: activeConv || showNew ? 260 : '100%', borderBottom: `1px solid ${theme.border}`, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {listPane}
          </div>
        )}
        {(activeConv || showNew || directLoading) && mainPane}
      </div>
    </div>
  );
};

export default ChatDockInner;
